// src/docs/docs.client.ts — pagina #/docs: GHID DE UTILIZARE (F6-T7, REQ-PIN-008), pe DOUĂ voci:
// „Ghid cetățean" (portalul public /embed + solicitări — linkul „Ajutor" din portal aterizează aici)
// și „Ghid funcționar" (back-office). Fără conținut tehnic (API/SQL/JSON-RPC) — detaliile de
// dezvoltare trăiesc în repo (CLAUDE.md / SPECS.md). Pagina răspunde la „cum fac X în aplicație?".
// Deep-link pe secțiune: #/docs?s=<id> (fragmentul clasic e ocupat de hash-router).

import "../styles.css"
import van from "vanjs-core"
import { session } from "../lib/session.client"

const t = van.tags

type Guide = "cetatean" | "functionar" | "api"

/** Audiența implicită: membru al unui org sau rol de platformă = staff → ghidul funcționarului. */
function is_staff(): boolean {
	return session.val.memberships.length > 0 || session.val.user?.platform_role != null
}

// Scroll lin la o secțiune. preventDefault oprește navigarea hash (router-ul ar
// interpreta #ce-este ca rută); fără JS, ancora face fallback nativ.
function scroll_to_id(id: string) {
	return (e: Event) => {
		e.preventDefault()
		document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
	}
}

// ─────────────────────────────────────────────
// Table of contents entries — grupate pe cele două ghiduri
// ─────────────────────────────────────────────
const TOC_GROUPS: Array<{
	guide: Guide
	title: string
	items: Array<{ id: string; label: string }>
}> = [
	{
		guide: "cetatean",
		title: "Ghid cetățean",
		items: [
			{ id: "cetatean", label: "Portalul public" },
			{ id: "cetatean-harta", label: "Harta publică" },
			{ id: "cetatean-regim", label: "Regimul urbanistic" },
			{ id: "cetatean-solicitari", label: "Solicitări către primărie" },
			{ id: "cetatean-consultare", label: "Consultarea publică" },
			{ id: "cetatean-cont", label: "Contul tău și notificările" },
		],
	},
	{
		guide: "functionar",
		title: "Ghid funcționar",
		items: [
			{ id: "ce-este", label: "Ce este Urbanism" },
			{ id: "primii-pasi", label: "Primii pași" },
			{ id: "harta", label: "Harta" },
			{ id: "coordonate", label: "Coordonate Stereo 70" },
			{ id: "raza", label: "Interogare pe o rază" },
			{ id: "zonificare", label: "Zonificarea actuală" },
			{ id: "lipire", label: "Lipirea la obiecte" },
			{ id: "solicitari-operator", label: "Solicitările cetățenilor" },
			{ id: "rapoarte", label: "Rapoarte" },
			{ id: "documente", label: "Documente" },
			{ id: "registre", label: "Registre" },
			{ id: "sabloane", label: "Șabloane Word" },
			{ id: "admin-publicare", label: "Publicare & Ordin 904" },
			{ id: "export-import", label: "Export și import (formate)" },
			{ id: "fundaluri", label: "Fundaluri de hartă" },
			{ id: "iconite", label: "Iconițe pentru puncte" },
			{ id: "liste-valori", label: "Liste de valori" },
			{ id: "filtre-salvate", label: "Filtre salvate" },
			{ id: "avize", label: "Nomenclatorul de avize" },
			{ id: "topologie", label: "Verificarea topologică" },
			{ id: "departamente", label: "Departamente & drepturi" },
			{ id: "notificari-email", label: "Notificările pe email" },
			{ id: "drepturi-strat", label: "Drepturi pe strat & atribut" },
			{ id: "asistent-ai", label: "Asistentul AI" },
			{ id: "suport", label: "Ajutor și suport" },
		],
	},
	{
		guide: "api",
		title: "Integrare & API",
		items: [
			{ id: "api-ogc", label: "OGC API Features (QGIS)" },
			{ id: "api-date", label: "GeoJSON & tile-uri MVT" },
			{ id: "api-export", label: "Export: formate & atașamente" },
			{ id: "api-interogari", label: "Filtre, rază, zonificare" },
			{ id: "api-embed", label: "Embed iFrame configurabil" },
			{ id: "api-linkuri", label: "API-uri definibile (linkuri)" },
			{ id: "api-stabilitate", label: "Ce e stabil, ce e intern" },
		],
	},
]

/** Ghidul căruia îi aparține o secțiune (pentru comutarea automată la deep-link ?s=). */
const SECTION_GUIDE: Record<string, Guide> = Object.fromEntries(
	TOC_GROUPS.flatMap((g) => g.items.map((i) => [i.id, g.guide])),
)

// ─────────────────────────────────────────────
// Primitive helpers
// ─────────────────────────────────────────────

function Code(code: string) {
	return t.code(
		{
			class:
				"font-mono text-sm bg-slate-100 dark:bg-slate-800 text-cyan-800 dark:text-cyan-300 rounded px-1.5 py-0.5",
		},
		code,
	)
}

function Pre(code: string) {
	return t.pre(
		{
			class:
				"bg-slate-900 text-slate-100 rounded-lg p-4 overflow-auto text-sm leading-relaxed mt-3 mb-1",
		},
		t.code(code),
	)
}

function H2(id: string, text: string) {
	return t.h2(
		{
			id,
			class:
				"text-2xl font-bold text-slate-900 dark:text-slate-100 mt-14 mb-4 scroll-mt-20 flex items-center gap-2",
		},
		t.a(
			{
				href: `#${id}`,
				onclick: scroll_to_id(id),
				class: "text-cyan-700 dark:text-cyan-300 hover:text-cyan-800 font-mono text-lg",
			},
			"#",
		),
		text,
	)
}

function H3(text: string) {
	return t.h3({ class: "text-lg font-semibold text-slate-800 dark:text-slate-200 mt-8 mb-2" }, text)
}

function P(...children: (string | HTMLElement)[]) {
	return t.p({ class: "text-slate-600 dark:text-slate-300 leading-relaxed mb-3" }, ...children)
}

/** Listă numerotată „pas cu pas" — elementul de bază al ghidului. */
function Steps(...items: (string | HTMLElement | (string | HTMLElement)[])[]) {
	return t.ol(
		{ class: "list-none flex flex-col gap-2.5 my-4" },
		...items.map((item, i) =>
			t.li(
				{ class: "flex items-start gap-3" },
				t.span(
					{
						class:
							"flex-shrink-0 w-6 h-6 rounded-full bg-cyan-700 text-white text-xs font-bold flex items-center justify-center mt-0.5",
					},
					String(i + 1),
				),
				t.span(
					{ class: "text-slate-600 dark:text-slate-300 leading-relaxed" },
					...(Array.isArray(item) ? item : [item]),
				),
			),
		),
	)
}

/** Notă evidențiată (sfat / atenționare). */
function Note(kind: "info" | "warn", ...children: (string | HTMLElement)[]) {
	const cls =
		kind === "warn"
			? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-200"
			: "border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950 text-cyan-900 dark:text-cyan-200"
	return t.div(
		{ class: `rounded-lg border px-4 py-3 text-sm leading-relaxed my-4 ${cls}` },
		...children,
	)
}

/** Captură de ecran cu legendă (fișierele stau în public/docs/, servite la /public/docs/…).
 *  Folosită DOAR pentru Google Play (decizia 04.09: ghidul din aplicație fără capturi; ele stau în
 *  manualele PDF din livrare). */
function Shot(file: string, caption: string) {
	return t.figure(
		{ class: "my-4" },
		t.img({
			src: `/public/docs/${file}`,
			alt: caption,
			loading: "lazy",
			class:
				"rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm max-w-full h-auto bg-white",
		}),
		t.figcaption({ class: "text-xs text-slate-500 dark:text-slate-400 mt-1.5" }, caption),
	)
}

/** Separatorul mare dintre cele două ghiduri (cetățean / funcționar). */
function GuideHeading(emoji: string, title: string, sub: string) {
	return t.div(
		{ class: "mt-16 mb-2 first:mt-0" },
		t.div(
			{
				class:
					"inline-flex items-center gap-2 rounded-full border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950 px-3.5 py-1.5 text-sm font-medium text-cyan-800 dark:text-cyan-300",
			},
			emoji,
			title,
		),
		t.p({ class: "mt-3 text-slate-600 dark:text-slate-300" }, sub),
	)
}

// ─────────────────────────────────────────────
// Sections — GHID CETĂȚEAN (portalul public /embed + solicitări)
// ─────────────────────────────────────────────

function SectionCetatean() {
	return t.section(
		{},
		H2("cetatean", "Portalul public al primăriei"),
		P(
			"Primăria publică harta documentației de urbanism printr-un link public — îl găsești de ",
			"regulă pe site-ul primăriei (harta e integrată direct în pagină) sau îl primești ca link. ",
			t.b(
				{ class: "text-slate-800 dark:text-slate-200" },
				"Nu ai nevoie de cont ca să consulți harta",
			),
			" — contul e necesar doar dacă vrei să trimiți o solicitare.",
		),
		P(
			"Primăria alege exact ce straturi și ce informații sunt vizibile public — dacă un strat sau ",
			"un detaliu nu apare, înseamnă că nu a fost publicat.",
		),
		H3("Aplicația pe telefon"),
		Shot("instalare.png", "Prima pagină: descărcarea aplicației din Google Play (buton + cod QR)."),
		P(
			"Harta publică merge în orice browser, dar o poți și instala ca aplicație: pe Android din ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "Google Play"),
			" (butonul și codul QR de pe prima pagină), sau — pe orice telefon — din meniul ",
			Code("▾"),
			" din colțul de sus, butonul ",
			Code("⤓ Instalează"),
			". Pe iPhone: în Safari, ",
			Code("Partajează → Adaugă pe ecranul principal"),
			". Aplicația instalată pornește și fără semnal și deschide direct harta primăriei; ",
			"harta și salvarea datelor au însă nevoie de conexiune.",
		),
	)
}

function SectionCetateanHarta() {
	return t.section(
		{},
		H2("cetatean-harta", "Harta publică: consultare și măsurare"),
		H3("Straturi și elemente"),
		P(
			"Panoul de straturi e un sertar lateral: extins, arată lista straturilor (zonificare, parcele, ",
			"rețele…) cu comutatoare; restrâns, devine o bară de simboluri — treci cu mouse-ul peste un ",
			"simbol ca să vezi numele stratului, click ca să-l aprinzi/stingi. Butonul ",
			Code("Legendă"),
			" afișează direct pe hartă simbolurile straturilor vizibile, iar câmpul de căutare din panou ",
			"filtrează atât straturile după nume, cât și elementele după valorile atributelor ",
			"(de ex. un număr cadastral).",
		),
		P(
			"Click pe orice element de pe hartă deschide panoul cu detaliile lui. Primăria alege ce ",
			"atribute sunt vizibile public — dacă un detaliu lipsește, nu a fost publicat.",
		),
		P(
			"Dacă primăria și-a configurat propriile fundaluri (de ex. ortofotoplanul), butoanele lor iau ",
			"locul perechii Hartă/Satelit din bara de sus. Straturile de puncte pot avea iconițe proprii — le ",
			"vezi și în legendă.",
		),
		H3("Căutare după adresă"),
		P(
			"Scrie o adresă în câmpul de căutare și apasă Enter — alegi din lista de rezultate și harta ",
			"zboară acolo, cu un marker pe punct. Merge și cu denumiri apropiate („Strada Principală 10”).",
		),
		H3("Coordonate, căutare pe rază și zonificarea în vigoare"),
		P(
			"Pe calculator, în colțul din dreapta-jos apar coordonatele cursorului — ",
			Code("X = … Y = …"),
			" în Stereo 70 (sistemul oficial; X = nord, Y = est) și, după bară, longitudinea și latitudinea. ",
			"Iconița de alături le copiază pe amândouă („Copiat”). Click pe un element: panoul lui are blocul ",
			Code("Coordonate"),
			" sau ",
			Code("Centrul geometriei"),
			" cu butonul ",
			Code("Copiază"),
			" — le poți trimite proiectantului sau primăriei.",
		),
		P(
			"În căutare, ",
			Code("Avansat ▾"),
			" → ",
			Code("Pe o rază de"),
			" (presetări de la 50 la 1000 m, maxim 5 000): ",
			Code("Interogare pe punct"),
			" arată tot ce e la cel mult atâția metri de punctul apăsat, cu un cerc pe hartă și distanța ",
			"fiecărui element; căutarea după atribute cu raza activă caută în jurul centrului hărții.",
		),
		P(
			"Rândul ",
			Code("Zonificare actuală (tematic)"),
			" din panoul de straturi (acolo unde primăria a publicat zonificarea) arată zonificarea în ",
			"vigoare: PUG-ul, cu zonele PUZ/PUD aprobate decupate și desenate deasupra, cu contur gros și ",
			"eticheta actului. Click pe o zonă îți spune codul, denumirea și sursa.",
		),
		H3("Documentația scrisă și vederea 3D"),
		P(
			"Deasupra listei de straturi, cardul ",
			Code("Documentația PUG"),
			" adună piesele scrise pe care primăria le-a făcut publice (memorii, regulamente scanate) — ",
			"le deschizi direct din hartă. Iar acolo unde primăria a activat-o, butonul ",
			Code("3D"),
			" înclină harta și ridică clădirile la înălțimea lor reglementată — cu ",
			Code("Teren"),
			" pentru relief, unde există.",
		),
		H3("Tipărire și salvare ca PDF"),
		P(
			"Butonul ",
			Code("Tipărește"),
			" din bara de sus deschide o pagină de tipărire cu vederea curentă a hărții (fundal, straturile ",
			"vizibile, ce ai desenat), legenda, bara de scară, nordul și data. Din dialogul browserului o ",
			"trimiți la imprimantă sau o salvezi ca PDF; poți adăuga un titlu sau descărca imaginea PNG pentru ",
			"o prezentare. Pinul de adresă/sesizare nu apare în captură. Documentul e informativ — nu ",
			"ține loc de certificat de urbanism.",
		),
		H3("Măsurare și desen pe hartă"),
		Steps(
			[
				"Din bara de sus alege ",
				Code("Distanță"),
				", ",
				Code("Suprafață"),
				" sau ",
				Code("Desen"),
				".",
			],
			["Click pe hartă pentru fiecare punct/colț; dublu-click încheie măsurătoarea sau forma."],
			[
				"La ",
				Code("Desen"),
				" poți schița mai multe forme una după alta; un click înapoi pe primul punct înainte de dublu-click închide o zonă.",
			],
			[
				"Rezultatul apare în colțul hărții; Esc renunță. Nimic nu se salvează — e doar pe ecranul tău.",
			],
		),
	)
}

function SectionCetateanRegim() {
	return t.section(
		{},
		H2("cetatean-regim", "„Ce pot construi aici?” — regimul urbanistic"),
		Steps(
			["Apasă butonul ", Code("Regim"), " din bara de sus a hărții."],
			["Click pe punctul care te interesează — oriunde pe hartă, fără să selectezi un strat."],
			[
				"Panoul din dreapta arată zona (UTR), indicatorii urbanistici (POT, CUT, regim de înălțime), ",
				"utilizările admise/interzise și, unde există, fișa completă din regulament.",
			],
		),
		P(
			"Dacă peste punct există o derogare aprobată (PUZ/HCL), ea apare ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "prima"),
			" — reglementările derogării prevalează asupra regimului de bază.",
		),
		P(
			"Sub indicatori, ",
			Code("Avize și acorduri de care vei avea nevoie"),
			" listează avizele cerute în zona respectivă (de ex. de la mediu, de la sănătate publică, de la ",
			"furnizorii de utilități) plus cele generale ale localității — dacă primăria le-a completat. În ",
			"extravilan apar doar cele generale.",
		),
		Note(
			"warn",
			"Informațiile din panou au caracter informativ — nu țin loc de certificat de urbanism. ",
			"Pentru un act oficial, folosește butonul „Fă o solicitare pentru acest punct” din panou.",
		),
	)
}

function SectionCetateanSolicitari() {
	return t.section(
		{},
		H2("cetatean-solicitari", "Solicitări către primărie"),
		P(
			"Cu un cont (înregistrare simplă, cu email și parolă) poți trimite primăriei sesizări, ",
			"cereri de certificat de urbanism de informare, avize sau observații — și le urmărești ",
			"pe toate în ",
			Code("Solicitările mele"),
			".",
		),
		H3("Depunerea din harta publică"),
		Steps(
			[
				"Din hartă: ",
				Code("✎ Fă o solicitare"),
				" (generală), ",
				Code("📍 pin"),
				" (alegi locația pe hartă — pinul se poate trage pentru ajustare), ",
				Code("📍 La locația mea"),
				" (GPS) sau „Sesizare despre acest element” din panoul unui element selectat.",
			],
			["Formularul se deschide pre-completat cu primăria, harta și locația/elementul ales."],
			["Completezi subiectul și descrierea, atașezi opțional fotografii sau documente și trimiți."],
		),
		H3("Urmărirea solicitării"),
		P(
			"Fiecare solicitare are o stare (nouă → în lucru → răspunsă/emisă/închisă) și un fir de ",
			"mesaje cu primăria — primești răspunsurile acolo și poți continua conversația. Când ",
			"primăria emite un act pentru cererea ta, îl descarci direct din pagina solicitării.",
		),
	)
}

function SectionCetateanConsultare() {
	return t.section(
		{},
		H2("cetatean-consultare", "Consultarea publică (PUG în dezbatere)"),
		P(
			"Când primăria pune o documentație în consultare publică, pe harta publică apare un banner ",
			"cu numele documentației și termenul până la care poți trimite observații.",
		),
		Steps(
			[
				Code("✍ Trimite o observație"),
				" deschide formularul deja legat de consultarea respectivă — scrii observația și o trimiți.",
			],
			[
				Code("📍 cu locație pe hartă"),
				" te lasă să alegi întâi punctul vizat (pin tras-abil), apoi observația pleacă împreună cu coordonata.",
			],
			[
				"Observația intră la primărie ca solicitare — îi urmărești starea și răspunsul în contul tău.",
			],
		),
	)
}

// ─────────────────────────────────────────────
// Sections — GHID FUNCȚIONAR (back-office)
// ─────────────────────────────────────────────

function SectionCetateanCont() {
	return t.section(
		{},
		H2("cetatean-cont", "Contul tău: parola și notificările"),
		H3("Am uitat parola"),
		P(
			"Pe pagina de autentificare apasă ",
			Code("Am uitat parola"),
			", scrie emailul contului și primești un link (valabil 60 de minute, folosibil o singură dată) ",
			"cu care setezi o parolă nouă. Pagina deschisă din link îți spune imediat dacă linkul a expirat ",
			"sau a fost deja folosit, ca să ceri altul. Din motive de siguranță, mesajul de confirmare e ",
			"același indiferent dacă emailul există sau nu — verifică și folderul Spam.",
		),
		H3("Ce notificări primești pe email"),
		P(
			"Pentru solicitările tale, primăria îți scrie automat când: solicitarea a fost preluată, ",
			"ai un mesaj nou, starea s-a schimbat (în lucru, răspuns, respinsă — cu motivul) ",
			"sau actul cerut a fost emis și e gata de descărcat. Fiecare email are linkul solicitării; ",
			"te autentifici și vezi conversația completă. Într-o conversație cu multe mesaje primești cel ",
			"mult un email pe oră — conținutul mesajelor îl citești în cont.",
		),
		Note(
			"info",
			"Emailurile vin de la „Urbanism by Vego”, în numele primăriei. Nu conțin datele operatorilor și nu au atașamente — actele se descarcă doar din cont.",
		),
		H3("Datele tale"),
		P(
			"Ce date prelucrează platforma, cine le vede și cât timp se păstrează scrie în ",
			t.a(
				{
					href: "/confidentialitate",
					target: "_blank",
					rel: "noopener",
					class: "underline hover:text-slate-900 dark:hover:text-slate-100",
				},
				"Politica de confidențialitate",
			),
			". Solicitările depuse sunt înregistrări ale primăriei; contul e al tău și îl poți șterge oricând, ",
			"urmând pașii din ",
			t.a(
				{
					href: "/confidentialitate#stergerea-contului",
					target: "_blank",
					rel: "noopener",
					class: "underline hover:text-slate-900 dark:hover:text-slate-100",
				},
				"secțiunea „Ștergerea contului”",
			),
			".",
		),
	)
}

function SectionCeEste() {
	return t.section(
		{},
		H2("ce-este", "Ce este Urbanism"),
		P(
			"Urbanism este platforma prin care primăria lucrează cu documentațiile de urbanism ",
			"(PUG, PUZ) digitalizate: o hartă interactivă a localității, documente emise direct ",
			"din șabloane oficiale și registre cu numerotare automată.",
		),
		t.div(
			{ class: "grid sm:grid-cols-3 gap-4 my-6" },
			...(
				[
					[
						"🗺️ Harta vie",
						"Toate straturile PUG (zonificare, UTR, parcele, rețele) într-o hartă pe care o poți interoga: ce reglementări se aplică aici?",
					],
					[
						"📄 Documente cu număr",
						"Certificate de urbanism și autorizații emise din șabloane Word, cu numere alocate automat din registrele primăriei.",
					],
					[
						"🕘 Istoric complet",
						"Nicio modificare nu se pierde: fiecare entitate și fiecare document își păstrează toate versiunile, cu autor și dată.",
					],
				] as const
			).map(([h, p]) =>
				t.div(
					{
						class:
							"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5",
					},
					t.h3({ class: "font-bold text-slate-900 dark:text-slate-100 mb-1" }, h),
					t.p({ class: "text-sm text-slate-600 dark:text-slate-300 leading-relaxed" }, p),
				),
			),
		),
	)
}

function SectionPrimiiPasi() {
	return t.section(
		{},
		H2("primii-pasi", "Primii pași: alege harta de lucru"),
		P(
			"Totul în aplicație se întâmplă în contextul unei hărți (localități) — ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "harta de lucru"),
			". O alegi o singură dată și rămâne selectată (inclusiv după închiderea browserului).",
		),
		Steps(
			[
				"După autentificare ajungi în ",
				Code("Spații de lucru"),
				" — hărțile disponibile, drept carduri; apasă pe cardul localității tale.",
			],
			[
				"Sau deschide meniul de utilizator (dreapta-sus, pe numele tău) — sub nume vezi harta de ",
				"lucru curentă și o poți schimba de pe orice pagină, fără să navighezi.",
			],
			[
				"Din acel moment, paginile Hartă, Documente și Registre arată DOAR conținutul hărții alese.",
			],
		),
		Note(
			"info",
			"Schimbarea hărții de lucru schimbă instant tot contextul — listele de documente și registrele se reîncarcă pentru noua localitate.",
		),
		Note(
			"info",
			"Pe telefon (pentru teren): instalează aplicația — pe Android din Google Play sau, pe orice telefon, din meniul de utilizator (▾) → „⤓ Instalează”; pe iPhone din Safari → Partajează → „Adaugă pe ecranul principal”. Te autentifici cu același cont.",
		),
	)
}

function SectionHarta() {
	return t.section(
		{},
		H2("harta", "Harta: căutare și interogare"),
		H3("Navigare și straturi"),
		P(
			"Pagina ",
			Code("Hartă"),
			" arată straturile documentației (zonificare, UTR, parcele, rețele…). Panoul de straturi e ",
			"un sertar: extins — listă cu comutatoare per strat; restrâns — bară de simboluri (tooltip cu ",
			"numele, click = aprinde/stinge). Butonul ",
			Code("solo"),
			" al unui strat îl lasă vizibil doar pe el; ",
			Code("Legendă"),
			" pune simbolurile straturilor vizibile direct pe hartă. Click pe orice entitate deschide ",
			"panoul cu proprietățile ei — entitatea selectată e evidențiată cu contur portocaliu.",
		),
		P(
			"Tot din panoul de straturi aprinzi și straturile tematice ale primăriei: ",
			Code("Acte"),
			" (actele hărții — certificate, autorizații, documente înregistrate — punctate pe hartă și ",
			"colorate după stare: verde = emis, gri = pregătit dar neemis, roșu = anulat; click = detaliile actului și linkul spre cerere) și ",
			Code("Solicitări"),
			" (cererile cetățenilor, colorate după stare) — click pe un punct sare direct la cererea lui. ",
			"Butonul 🌙/☀️ din bara de sus comută între tema luminoasă și cea întunecată.",
		),
		H3("Căutare unificată"),
		Steps(
			[
				"Câmpul de căutare din panou caută în ACELAȘI loc straturi după nume și elemente după ",
				"valorile atributelor (ex. un număr cadastral).",
			],
			["Dacă există o singură potrivire de element, harta zboară direct la el și îl evidențiază."],
			["La mai multe potriviri, alegi din listă."],
		),
		H3("„Ce reglementări se aplică aici?” (interogare punct)"),
		Steps(
			[
				"În căutarea din panoul lateral apasă ",
				Code("Avansat ▾"),
				" → ",
				Code("Interogare pe punct (click pe hartă)"),
				".",
			],
			["Apasă oriunde pe hartă."],
			[
				"Primești atributele TUTUROR straturilor care se suprapun în acel punct: zonă funcțională, UTR, regim de înălțime etc. ",
				"Cu o rază setată la „Pe o rază de”, și elementele din apropiere, cu distanța lor (vezi „Interogare pe o rază”).",
			],
		),
		H3("Vedere 3D, teren și însorire"),
		P(
			"Butonul ",
			Code("3D"),
			" înclină harta și extrudează clădirile după înălțimea din atribute sau din regimul UTR ",
			"(configurabil per strat, în Admin). În modul 3D apar și ",
			Code("Teren"),
			" (relieful, dacă instanța are sursă de altimetrie) și ",
			Code("Soare"),
			" — alegi data și ora, iar lumina cade ca în realitate: utile pentru discuții despre ",
			"înălțimi și umbrire.",
		),
		H3("Documentele parcelei"),
		P(
			"În panoul entității selectate apare secțiunea ",
			Code("Documente"),
			" — toate documentele legate de parcela respectivă, cu numerele lor de registru. ",
			"De acolo pornești direct emiterea unui act nou (",
			Code("Generează: …"),
			") — lista actelor disponibile depinde de strat: straturile UTR oferă implicit certificatul ",
			"de urbanism, iar pentru celelalte administratorul hărții alege actele permise în ",
			Code("Admin"),
			", la câmpul „Acte permise” al stratului. Un strat fără acte configurate nu oferă nimic.",
		),
		H3("Editarea hărții (pentru editori)"),
		P(
			"Harta nu e doar de privit: conturile cu drept de editare au butonul ",
			Code("Editare"),
			" în bara hărții — adaugi și corectezi elemente direct pe hartă, fără re-import.",
		),
		Steps(
			[
				"Element nou: apasă ",
				Code("Editare"),
				", alege stratul, apoi forma de desenat (platforma o oferă pe cea potrivită stratului — ",
				"poligon, linie sau punct). Click pe hartă pentru fiecare punct, dublu-click termină forma, ",
				"Esc renunță; completezi atributele stratului și apeși ",
				Code("Salvează elementul"),
				".",
			],
			[
				"Element existent: îl selectezi, iar în panoul din dreapta, sub atribute, apare secțiunea ",
				Code("Editare"),
				" — ",
				Code("Editează atributele"),
				" (valorile devin câmpuri, se pot adăuga și atribute noi), ",
				Code("Editează geometria"),
				" (tragi de punctele conturului chiar pe hartă; punctele de mijloc adaugă vertecși) și ",
				Code("Șterge"),
				", cu confirmare.",
			],
		),
		Note(
			"info",
			"Nimic nu se pierde: fiecare modificare creează o versiune nouă, cu autor și dată, iar din ",
			"istoricul elementului butonul „Revino la v1, v2…” readuce oricând geometria și atributele ",
			"unei versiuni anterioare. Elementele compuse din mai multe contururi separate (rare, din ",
			"importuri CAD) nu se pot edita pe hartă — corectarea lor se face prin re-import.",
		),
		H3("Colectarea din teren (pe telefon)"),
		P(
			"Aceeași editare merge și pe telefon, cu două ajutoare de teren: poziția GPS și pozele. ",
			"Datele ajung direct în harta primăriei, cu amprenta de timp, autor și precizie GPS scrise ",
			"automat (nu se pot modifica din formular).",
		),
		Steps(
			[
				"Apasă ",
				Code("Editare"),
				" și alege stratul de colectare din listă — sau ",
				Code("+ Strat nou de colectare"),
				" (nume, tip: punct / linie / poligon, câteva câmpuri simple) dacă ai o campanie nouă: ",
				"stratul apare pe loc, fără să treci prin Admin.",
			],
			[
				"Pentru straturile de puncte: ",
				Code("📍 Locația mea"),
				" pune punctul exact unde ești (precizia GPS e afișată; peste 30 m primești un ",
				"avertisment — poți trage punctul manual). Pentru linii/poligoane desenezi pe hartă, ca ",
				"pe calculator.",
			],
			["Completezi atributele și apeși ", Code("Salvează elementul"), "."],
			[
				"Poze: selectezi elementul, iar în panoul lui, la ",
				Code("Poze de teren"),
				", apeși ",
				Code("📷 Fotografiază"),
				" (camera) sau ",
				Code("🖼 Din galerie"),
				" — până la 10 poze per element, comprimate în telefon înainte de trimitere (fără ",
				"date de localizare din fișier). Ștergerea se face din ",
				Code("Gestionează"),
				", cu confirmare.",
			],
		),
		Note(
			"info",
			"Colectarea are nevoie de conexiune de date în momentul salvării; într-o zonă fără semnal harta nu se afișează. Lucrul complet offline (cu sincronizare la revenirea semnalului) nu face parte din această versiune — se poate adăuga ulterior, ca extindere.",
		),
		H3("Interogare sisteme externe"),
		P(
			"Dacă administratorul hărții a definit linkuri către alte sisteme (de ex. o interogare ",
			"cadastrală după numărul parcelei), în panoul elementului selectat apare secțiunea ",
			Code("Interogare sisteme externe"),
			": un click deschide sistemul respectiv gata completat cu datele elementului — ",
			"coordonate sau atribute. Cum se definesc: vezi „Publicare, regulament și Ordin 904”.",
		),
		H3("Tipărire și salvare ca PDF"),
		P(
			"Butonul ",
			Code("Tipărește"),
			" din bara de sus deschide o pagină de tipărire cu vederea curentă a hărții (fundal, straturile ",
			"vizibile, schițele de măsurare, vederea 3D), legenda, bara de scară, nordul și data. Din dialogul browserului o ",
			"trimiți la imprimantă sau o salvezi ca PDF; poți adăuga un titlu sau descărca imaginea PNG pentru ",
			"o prezentare. Pinul de adresă/sesizare nu apare în captură. Documentul e informativ — nu ",
			"ține loc de certificat de urbanism.",
		),
		H3("Link partajabil"),
		P(
			"Adresa paginii se actualizează cu selecția curentă — copiezi link-ul din browser și ",
			"colegul care îl deschide vede exact aceeași parcelă, evidențiată și centrată.",
		),
	)
}

// ─────────────────────────────────────────────
// Harta — faza 2 (coordonate, rază, zonificare actuală, lipire). Paritate 1:1 în /embed pentru
// tot ce e read-only; lipirea e doar în editare.
// ─────────────────────────────────────────────

function SectionCoordonate() {
	return t.section(
		{},
		H2("coordonate", "Coordonate Stereo 70 și WGS84"),
		P(
			"Sistemul oficial de coordonate al României este Stereo 70 — în el sunt scrise inventarele de ",
			"coordonate, documentațiile cadastrale și planurile proiectanților. Harta îți arată coordonatele ",
			"direct în acest sistem, fără conversii făcute de mână.",
		),
		H3("Bara de coordonate"),
		P(
			"Pe calculator, în colțul din dreapta-jos al hărții (deasupra barei de scară) stă bara de ",
			"coordonate: ",
			Code("X = … Y = … | lng, lat"),
			" — poziția cursorului în Stereo 70 și, după bară, în WGS84 (longitudine, latitudine). Se ",
			"actualizează pe măsură ce miști mouse-ul. Iconița de copiere de lângă ea pune ambele scrieri în ",
			"clipboard (apare „Copiat”), pe două rânduri: ",
			Code("Stereo 70: X = … Y = …"),
			" și ",
			Code("WGS84: lng, lat"),
			".",
		),
		Note(
			"warn",
			"Notația cadastrală românească are X = nord și Y = est — invers față de x/y din programele GIS. ",
			"Bara și textul copiat respectă notația cadastrală, ca să poți lipi valorile direct într-un inventar ",
			"de coordonate. Când pui aceleași valori într-un CSV cu coloane x/y, coloana x primește Y-ul (estul), ",
			"iar coloana y primește X-ul (nordul).",
		),
		P(
			"Pe telefon nu există cursor, deci bara nu apare; coordonatele elementelor rămân în panoul lor.",
		),
		H3("Coordonatele unui element"),
		P(
			"Click pe orice element: panoul din dreapta are blocul ",
			Code("Coordonate"),
			" (la puncte) sau ",
			Code("Centrul geometriei"),
			" (la linii și poligoane), cu ambele scrieri și butonul ",
			Code("Copiază"),
			". La interogarea pe punct, panoul rezultatelor arată ",
			Code("Punct interogat"),
			" cu aceleași coordonate. Dacă un punct cade în afara țării (de ex. un obiect georeferențiat ",
			"greșit), în locul numerelor Stereo 70 apare „în afara României”; linia WGS84 rămâne.",
		),
		P("Harta publică arată aceleași coordonate — cetățeanul sau proiectantul le copiază la fel."),
	)
}

function SectionRaza() {
	return t.section(
		{},
		H2("raza", "Interogare pe o rază: ce e în jurul unui punct"),
		P(
			"Interogarea pe punct arată în mod obișnuit doar elementele care ating exact punctul apăsat. Cu o ",
			"rază activă primești tot ce se află la cel mult atâția metri de punct — util pentru „ce rețele ",
			"trec pe lângă parcelă?” sau „ce obiective publice sunt în apropiere?”.",
		),
		Steps(
			["Deschide căutarea din panoul lateral și apasă ", Code("Avansat ▾"), "."],
			[
				"La ",
				Code("Pe o rază de"),
				" scrie distanța în metri sau apasă o presetare: 50, 100, 250, 500 sau 1000. Maximul este ",
				"5 000 m — o valoare mai mare e adusă automat la 5 000. Valoarea 0 revine la comportamentul ",
				"obișnuit (doar elementele care ating punctul).",
			],
			[
				"Apasă ",
				Code("Interogare pe punct (click pe hartă)"),
				" și dă click pe hartă. În jurul punctului apare un cerc punctat cu raza aleasă, iar panoul ",
				"din dreapta se intitulează „Intersecții … · pe o rază de 250 m” și arată distanța fiecărui ",
				"element („atinge” pentru cele care conțin punctul, „~118 m” pentru celelalte), de la cel mai ",
				"apropiat la cel mai depărtat.",
			],
			[
				"✕ pe panou închide rezultatele și șterge cercul. Modul de interogare se închide și când ",
				"închizi secțiunea „Avansat” sau panoul de căutare.",
			],
		),
		P(
			"Raza se aplică și căutării după atribute: cu o rază activă, căutarea devine „în apropiere” — ",
			"caută în jurul centrului hărții (cercul se mută acolo) și afișează distanța fiecărui rezultat. ",
			"Distanțele sunt reale, măsurate pe teren, nu pe ecran. Funcționează la fel în harta publică.",
		),
	)
}

function SectionZonificare() {
	return t.section(
		{},
		H2("zonificare", "Zonificarea actuală: PUG decupat de PUZ-urile aprobate"),
		P(
			"Când consiliul local aprobă un PUZ sau un PUD, reglementările lui prevalează asupra PUG-ului pe ",
			"zona respectivă. Stratul UTR importat din PUG rămâne însă neschimbat — e documentul aprobat ",
			"inițial. Rândul ",
			Code("Zonificare actuală (tematic)"),
			" le combină: zonificarea în vigoare azi, calculată pe loc din PUG minus derogări.",
		),
		H3("Ce trebuie pregătit"),
		P(
			"Harta are nevoie de un strat cu rolul de urbanism ",
			Code("UTR — unitățile teritoriale de referință (sursa regimului)"),
			" și, pentru derogări, de straturile PUZ/PUD marcate cu rolul ",
			Code("Derogare — PUZ aprobat (prevalează asupra regimului)"),
			" — ambele se aleg în ",
			Code("Admin"),
			" → Straturi → ",
			Code("Editare"),
			" → „Rol urbanism”. Fără strat UTR rândul nu apare; fără derogări, zonificarea actuală coincide ",
			"cu PUG-ul.",
		),
		H3("Pe hartă"),
		Steps(
			[
				"În panoul de straturi, după straturile hărții, găsești rândul ",
				Code("Zonificare actuală (tematic)"),
				" — apasă ",
				Code("ascuns"),
				" ca să-l aprinzi.",
			],
			[
				"Zonele PUG sunt colorate pe cod, cu porțiunile acoperite de derogări decupate; zonele PUZ/PUD ",
				"stau deasupra, cu contur gros punctat și eticheta „cod · act”. Subtitlul rândului spune ",
				"„PUG (strat) decupat de N derogări”.",
			],
			[
				"Click pe o zonă: fereastra arată codul, denumirea din regulament, sursa (PUG sau PUZ) și, la ",
				"derogări, actul și hotărârea; în panoul din dreapta se deschide poligonul-sursă (UTR-ul sau ",
				"PUZ-ul real), cu istoricul lui.",
			],
			[
				Code("Legendă"),
				" și ",
				Code("Tipărește"),
				" includ cele două rânduri: „PUG (decupat)” și „PUZ/derogare”.",
			],
		),
		Note(
			"info",
			"Stratul se recalculează la fiecare aprindere: după ce editezi sau importezi un PUZ, stinge și ",
			"aprinde rândul. În harta publică intră doar straturile publicate — un PUZ nepublicat nu decupează ",
			"PUG-ul și nu apare.",
		),
	)
}

function SectionLipire() {
	return t.section(
		{},
		H2("lipire", "Lipirea la obiecte în editare"),
		P(
			"Când desenezi un element nou lângă unul existent — o parcelă vecină, un tronson de rețea care îl ",
			"continuă pe altul — vrei ca punctele să cadă exact pe colțurile și laturile vecinului, nu la câțiva ",
			"centimetri distanță. Lipirea face asta singură.",
		),
		Steps(
			[
				"Apasă ",
				Code("Editare"),
				". În panoul de editare, butonul ",
				Code("Lipire: pornită"),
				" e activ implicit; un click îl trece pe ",
				Code("Lipire: oprită"),
				" (și înapoi), oricând — chiar în timpul desenului.",
			],
			[
				"La desen, când cursorul ajunge la cel mult 12 pixeli de un colț al unui obiect din stratul ",
				"editat, sare pe colț (marcaj chihlimbariu); dacă nu e un colț în apropiere, se lipește de cea ",
				"mai apropiată latură. Colțul are prioritate față de latură.",
			],
			[
				"Bifa ",
				Code("și la straturile vizibile"),
				" extinde lipirea la toate straturile aprinse pe hartă — de ex. desenezi o parcelă lipită de ",
				"axul unei străzi din alt strat.",
			],
			[
				"La ",
				Code("Editează geometria"),
				" (tragerea colțurilor unui element existent) lipirea funcționează la fel — de vecini, nu de ",
				"poziția veche a propriului contur.",
			],
		),
		P(
			"Toleranța e în pixeli de ecran, deci nu depinde de zoom: apropie harta pentru precizie mai mare. ",
			"Punctele se lipesc în momentul plasării. Se salvează geometria lipită; obiectele vecine nu se ",
			"modifică.",
		),
	)
}

function SectionSolicitariOperator() {
	return t.section(
		{},
		H2("solicitari-operator", "Solicitările cetățenilor (inbox-ul primăriei)"),
		P(
			"Pagina ",
			Code("Solicitări"),
			" e inbox-ul primăriei: toate cererile cetățenilor (sesizări, CU de informare, avize, ",
			"observații din consultare), filtrabile pe stare, tip și „ale mele”.",
		),
		H3("Fluxul unei cereri"),
		Steps(
			[
				"Preia cererea — devine automat a ta (",
				Code("Preluată"),
				") și răspunzi prin firul de mesaje, cu atașamente dacă e nevoie.",
			],
			[
				"Schimbă starea pe măsură ce avansezi: ",
				Code("În lucru"),
				" → ",
				Code("Răspunsă"),
				" / ",
				Code("Emisă"),
				" / ",
				Code("Închisă"),
				". Platforma permite doar tranzițiile legale; respingerea și redeschiderea cer un motiv, care rămâne în istoric.",
			],
			[
				"La cererile cu punct pe hartă, detaliul arată direct ",
				t.b({ class: "text-slate-800 dark:text-slate-200" }, "regimul urbanistic"),
				" al locației — nu mai deschizi harta separat.",
			],
			[
				"Pentru CU de informare/avize: secțiunea ",
				Code("Generează act din șablon"),
				" creează actul cu datele cererii și regimul completate automat; actul se atașează cererii, iar starea ",
				Code("Emisă"),
				" îl cere obligatoriu.",
			],
		),
		P(
			"Fiecare cerere are un istoric complet și auditabil: cine a preluat-o, ce stări a traversat, ",
			"când s-a atașat actul și de ce a fost respinsă/redeschisă. Observațiile din consultarea ",
			"publică apar și centralizate în pagina ",
			Code("Rapoarte"),
			", cu export CSV.",
		),
	)
}

function SectionNotificariOperator() {
	return t.section(
		{},
		H3("Notificări pe email (operator)"),
		P(
			"Când instanța are email configurat, primești automat un mesaj la fiecare ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "solicitare nouă"),
			" depusă către primăria ta și la ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "mesajele noi"),
			" de la cetățean (către operatorul care a preluat-o, sau către toți editorii dacă nu e ",
			"preluată) — cel mult un email pe oră per solicitare, fără textul cetățeanului: îl citești în ",
			"cerere. Cetățeanul e anunțat automat la preluare, la răspuns, la schimbarea stării și ",
			"la emiterea actului — fără să-i scrii tu și fără numele sau adresa ta. În inbox, fiecare ",
			"cerere arată „Ultimul mesaj: cetățean / primărie” — evidențiat când așteaptă răspunsul tău.",
		),
	)
}

function SectionRapoarte() {
	return t.section(
		{},
		H2("rapoarte", "Rapoarte: activitatea primăriei, dintr-o privire"),
		P(
			"Pagina ",
			Code("Rapoarte"),
			" agregă activitatea hărții de lucru: certificate emise pe luni, solicitări pe stare și pe ",
			"tip, bilanțul suprafețelor pe UTR (calculat geodezic), documentele pe registre și lista ",
			"observațiilor din consultarea publică.",
		),
		P(
			"Fiecare tabel are buton de ",
			Code("Export CSV"),
			" — fișierele se deschid corect direct în Excel (separator și diacritice pregătite pentru ",
			"varianta românească). Utile pentru raportările către conducere sau către consiliul local.",
		),
	)
}

function SectionDocumente() {
	return t.section(
		{},
		H2("documente", "Documente: emitere pas cu pas"),
		P(
			"Pagina ",
			Code("Documente"),
			" e registratura digitală a hărții de lucru: aici emiți documente din șabloane, ",
			"înregistrezi documente existente și le regăsești pe toate.",
		),
		H3("Emiterea unui certificat de urbanism"),
		Steps(
			["Deschide pagina Documente și alege șablonul (ex. Certificat de urbanism F.6)."],
			[
				"Alege registrul din care primește numărul (ex. Registru CU 2026) — numărul următor se alocă automat și intră singur în document.",
			],
			[
				"Leagă documentul de parcela din GIS și apasă ",
				Code("⚡ Auto-completează din entitate"),
				" — câmpurile cu corespondent în hartă (suprafață, UTR, regim tehnic…) se completează singure.",
			],
			["Completează restul câmpurilor (solicitant, scop, valabilitate…)."],
			[
				"Apasă ",
				Code("Generează documentul"),
				" și descarcă fișierul Word gata completat, cu numărul de registru pe el.",
			],
		),
		H3("Înregistrarea unui document existent"),
		P(
			"Ai un document făcut în afara platformei? Îl urci din formularul ",
			Code("Înregistrează document existent"),
			" — opțional primește și el un număr dintr-un registru.",
		),
		H3("Căutare și filtre"),
		P(
			"Bara de căutare găsește documentele după nume sau după numărul de registru (ex. ",
			Code("CU0003"),
			"); filtrele restrâng lista pe status, șablon sau registru.",
		),
		H3("Corecturi: rectificare, redenumire, anulare"),
		P(
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "Rectifică"),
			" — ai greșit un câmp într-un document emis? Rectificarea îl regenerează cu datele corectate, ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "păstrând același număr de registru"),
			". Versiunea veche rămâne în istoric.",
		),
		P(
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "Anulează"),
			" — documentele cu număr de registru nu se șterg niciodată: se anulează. Numărul rămâne ",
			"ocupat și apare în registru marcat ",
			t.span({ class: "text-red-700 dark:text-red-300 font-bold text-sm" }, "ANULAT"),
			" — exact ca într-un registru pe hârtie.",
		),
		P(
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "Istoric"),
			" — fiecare document își arată toate versiunile: când a fost creat, rectificat, redenumit sau anulat și de către cine.",
		),
		Note(
			"warn",
			"Ștergerea e permisă doar pentru documentele FĂRĂ număr de registru. Tot ce a primit un număr rămâne în evidență pentru totdeauna.",
		),
	)
}

function SectionRegistre() {
	return t.section(
		{},
		H2("registre", "Registrele de documente"),
		P(
			"Un registru numără automat documentele unei hărți: primul document primește ",
			Code("CU0001"),
			", al doilea ",
			Code("CU0002"),
			" și tot așa. Numerele nu se refolosesc niciodată — dacă un document e anulat, numărul lui rămâne ocupat.",
		),
		H3("Crearea unui registru"),
		Steps(
			[
				"Deschide pagina Registre și completează numele (ex. „Registru certificate de urbanism 2026”).",
			],
			[
				"Alege prefixul codului (ex. ",
				Code("CU"),
				") și, opțional, un sufix (ex. ",
				Code("/2026"),
				") — vezi pe loc cum va arăta primul număr.",
			],
			[
				"În „Setări avansate” poți lega registrul de un câmp de șablon (ex. ",
				Code("nr_certificat"),
				") — numărul intră automat în documentul Word la generare.",
			],
		),
		H3("An nou, registru nou"),
		P(
			"Numerotarea nu se resetează automat. Când începe un an nou, creezi pur și simplu un registru nou ",
			"(ex. „Registru CU 2027”, cu sufixul ",
			Code("/2027"),
			") — cel vechi rămâne închis și intact.",
		),
		H3("Evidența legală"),
		P(
			"Apasă ",
			Code("Vezi registrul"),
			" pe orice registru: tabelul arată toate numerele alocate în ordine, cu documentul, data și ",
			"statusul fiecăruia (inclusiv cele anulate). Fiecare document e descărcabil direct din tabel.",
		),
	)
}

function SectionSabloane() {
	return t.section(
		{},
		H2("sabloane", "Șabloanele Word"),
		P(
			"Documentele se generează din șabloane ",
			Code(".docx"),
			" obișnuite, editate în Word. Platforma vine cu formularele oficiale: ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "Certificat de urbanism (F.6)"),
			" și ",
			t.b(
				{ class: "text-slate-800 dark:text-slate-200" },
				"Autorizație de construire/desființare (F.11)",
			),
			", conform normelor metodologice.",
		),
		H3("Cum scrii un șablon"),
		P(
			"Oriunde în documentul Word vrei o valoare completată automat, scrii numele câmpului între acolade:",
		),
		Pre(
			`Se certifică pentru imobilul situat în {adresa_imobil},
identificat prin {identificare_imobil}, solicitat de {solicitant}.

Lista avizelor necesare:
{FOR a IN avize_specifice}
  • {$a.denumire}
{END-FOR a}`,
		),
		P(
			"După încărcare, platforma detectează singură câmpurile și le arată în formularul de generare.",
		),
		H3("Etichete și completare automată"),
		P(
			"Din butonul ",
			Code("Câmpuri"),
			" al fiecărui șablon poți seta, fără să atingi Word-ul: eticheta afișată în formular, ",
			"tipul câmpului, valoarea implicită și ",
			t.b({ class: "text-slate-800 dark:text-slate-200" }, "sursa de auto-completare"),
			" din harta GIS (ex. ",
			Code("attributes.suprafata"),
			" ia automat suprafața parcelei legate).",
		),
		P(
			"Antetul și semnăturile actelor (județul, autoritatea emitentă, conducătorul, secretarul general, ",
			"arhitectul-șef) se completează o singură dată, pe hartă: ",
			Code("Admin"),
			" → ",
			Code("Straturi"),
			" → ",
			Code("Date de emitent"),
			". De acolo intră automat în fiecare certificat, autorizație sau fișă generată; în formularul ",
			"actului le poți totuși schimba punctual.",
		),
		Note(
			"info",
			"Șabloanele sunt comune tuturor hărților — un singur formular F.6, folosit de orice localitate din platformă.",
		),
	)
}

function SectionAdminPublicare() {
	return t.section(
		{},
		H2("admin-publicare", "Publicare, regulament și Ordin 904"),
		P(
			"Pagina ",
			Code("Admin"),
			" e consola hărții de lucru: straturi și import de date, publicarea hărții către cetățeni, ",
			"regulamentul de urbanism și conformitatea pentru livrarea către minister.",
		),
		H3("Datele hărții (import)"),
		P(
			"Straturile se încarcă din fișierele GIS ale proiectantului (GeoPackage sau shapefile), din ",
			"formularul de import. La actualizări, re-importul cu potrivire detectează singur ce s-a ",
			"schimbat — versiuni noi doar pentru elementele modificate, nimic dublat — iar butonul ",
			Code("Previzualizează"),
			" arată planul (câte adăugate/modificate/șterse) înainte să scrii ceva.",
		),
		H3("Harta publică (portalul cetățeanului)"),
		Steps(
			[
				"În secțiunea de publicare bifezi straturile care devin vizibile public — restul rămân ",
				"interne, oricâte ar fi.",
			],
			[
				"Per strat public poți restrânge și ATRIBUTELE vizibile (bife per-atribut) — de ex. ",
				"publici parcelele, dar fără numele proprietarilor. Ce nu e bifat nu există pentru public: ",
				"nu apare în detalii, nu se găsește la căutare.",
			],
			[
				"Primești un link unic de tip embed — îl pui pe site-ul primăriei sau îl trimiți direct; ",
				"cetățeanul nu are nevoie de cont.",
			],
			[
				"Ai nevoie de mai multe variante? Secțiunea ",
				Code("Embed-uri configurabile (iFrame)"),
				" creează oricâte link-uri suplimentare, fiecare cu propriile straturi (doar dintre cele ",
				"publicate), instrumente bifabile (măsurare, legendă, căutare, 3D, regim, sesizări, ",
				"documente), opțional cu dată de expirare sau blocat la extentul hărții — de ex. o ",
				"variantă restrânsă pentru site-ul unui partener. Fiecare se revocă individual, fără să ",
				"le atingă pe celelalte.",
			],
		),
		Note(
			"warn",
			"Publicarea hărții e stăpâna: un embed poate doar să restrângă straturile publicate, niciodată să le extindă. Dacă republici harta fără un strat, dispare și din embed-urile care îl aveau — îl bifezi la loc și embed-urile își revin, cu aceleași link-uri.",
		),
		H3("Trimite harta pe email"),
		P(
			"Sub linkul public (și sub fiecare embed) există ",
			Code("✉ Trimite pe email"),
			": scrii adresa destinatarului și, opțional, un mesaj — persoana primește linkul hărții ",
			"(se deschide fără cont) și îți poate răspunde direct pe emailul tău. Disponibil doar când ",
			"instanța are email configurat.",
		),
		H3("Linkuri către sisteme externe (API-uri definibile)"),
		P(
			"Secțiunea ",
			Code("Interogare sisteme externe"),
			" definește butoane care apar în panoul oricărui element selectat (pe harta internă și în ",
			"portalul public): o etichetă + un URL-șablon cu locuri de completat — ",
			Code("{lng}"),
			" și ",
			Code("{lat}"),
			" pentru coordonatele elementului, ",
			Code("{attr:cheie}"),
			" pentru valoarea unui atribut (ex. ",
			Code("{attr:nr_cadastral}"),
			"). Opțional, limitezi butonul la anumite straturi. Doar adrese http(s).",
		),
		H3("Vederea 3D: de unde vine înălțimea clădirilor"),
		P(
			"Butonul 3D de pe hartă are nevoie de o sursă de înălțime pentru stratul de clădiri. În ",
			"formularul stratului (de regulă ",
			Code("fond_construit"),
			"), la ",
			Code("Extrudare 3D — sursa înălțimii"),
			", pui selectul pe „da” și alegi: un atribut cu înălțimea în metri, unul cu numărul de ",
			"etaje, un regim de tipul „P+2”, sau bifezi ",
			Code("înălțimea din regimul UTR"),
			" — clădirile fără niciun atribut iau regimul maxim al zonei din regulament. Pentru ",
			"hărțile importate din CAD (fără atribute de înălțime) ultima variantă e cea potrivită: ",
			"două click-uri și 3D-ul devine credibil.",
		),
		H3("Regulamentul (tabul „Regim UTR”)"),
		P(
			"Indicatorii urbanistici per UTR (POT, CUT, regim de înălțime), funcțiunile și fișele RLU se ",
			"întrețin din tabul ",
			Code("Regim UTR"),
			" — tot regulamentul într-un singur loc. Ele alimentează butonul „Regim” de pe hartă, ",
			"autocompletarea certificatelor și răspunsurile asistentului AI.",
		),
		H3("Conformitate Ordin 904/2023 (livrarea către MDLPA)"),
		P(
			"Ministerul cere PUG-ul digital într-un format standard (11 clase de straturi cu nume și ",
			"atribute fixe, proiecția Stereo 70). Platforma face traducerea:",
		),
		Steps(
			[
				"La fiecare strat relevant setezi ",
				Code("Clasa Ordin 904"),
				" din formularul stratului (ex. zonificarea propusă → ",
				Code("ZFPropusa"),
				") — raportul de conformitate îți sugerează maparea potrivită.",
			],
			[
				"Panoul ",
				Code("Conformitate Ordin 904"),
				" validează totul: clase nemapate, atribute obligatorii lipsă (cu numărul de elemente), ",
				"geometrii de reparat — și îți spune exact ce mai e de completat.",
			],
			[
				"Când raportul e verde, butonul ",
				Code("Descarcă GPKG 904"),
				" produce fișierul de livrare gata de încărcat pe platforma Date Locale a ministerului. ",
				"Cu probleme blocante, exportul e refuzat — nu poți livra din greșeală un fișier neconform.",
			],
		),
	)
}

// ─────────────────────────────────────────────
// Administrare — faza 2 (export/import în formate multiple, fundaluri, iconițe, liste de valori,
// filtre salvate, avize, topologie) + organizație (departamente, drepturi pe module, notificări).
// ─────────────────────────────────────────────

function SectionExportImport() {
	return t.section(
		{},
		H2("export-import", "Export și import în mai multe formate"),
		P(
			"Datele hărții ies din platformă în formatele pe care le cer proiectanții, alte instituții sau ",
			"programele de birou — și intră din aceleași formate.",
		),
		P(
			"Simbolurile planșei proiectantului se păstrează la import (culori, contururi, stilul pe valoare, hașurile) ",
			"și se scriu înapoi la export, în GeoPackage și Shapefile, ca stiluri QGIS; pe straturile needitate în platformă, GeoPackage-ul poartă chiar fișierul de stil al proiectantului, cu tot decorul pe care harta nu-l desenează. Harta din platformă este însă o ",
			"redare simplificată a planșei: hașurile se desenează pe patru direcții, cu linii de grosime fixă, iar ",
			"modelele de puncte, markerii de pe contur și a doua linie a unui simbol nu se desenează — apar ca o nuanță ",
			"a culorii lor. Regulile cu mai multe condiții se păstrează (atribute diferite legate cu „și”, liste, prefixe, ",
			"compararea fără majuscule), mai puțin cele cu funcții de text avansate, care cad pe culoarea de bază. Diferențele față de ",
			"afișarea din QGIS sunt limita acestui model, nu un defect al datelor.",
		),
		H3("Exportul"),
		Steps(
			[
				"În ",
				Code("Admin"),
				" → tabul Straturi, bara ",
				Code("Export hartă completă:"),
				" — alege formatul: ",
				Code("GeoPackage (.gpkg)"),
				", ",
				Code("Shapefile (arhivă .zip)"),
				", ",
				Code("CSV (coloana WKT + atribute)"),
				", ",
				Code("Excel (.xlsx, o foaie per strat)"),
				", ",
				Code("GML 3"),
				", ",
				Code("KML (Google Earth, WGS84)"),
				" sau, doar pentru membrii primăriei, ",
				Code("MBTiles (tile-uri vectoriale, zoom 8–15)"),
				".",
			],
			[
				"La „coordonate” alegi sistemul: ",
				Code("Stereo 70 (EPSG:3844)"),
				" (implicit — cel oficial), WGS84, Web Mercator sau alt cod EPSG. Selectul dispare la KML ",
				"(mereu WGS84) și la MBTiles (mereu Web Mercator).",
			],
			[
				"Bifa ",
				Code("cu atașamente (documente + poze de teren)"),
				" — doar pentru membrii primăriei — produce o arhivă zip cu fișierul de export, documentele ",
				"hărții, pozele de teren și un manifest. Un vizitator al unei hărți publice nu o vede.",
			],
			[
				"Apasă ",
				Code("Descarcă"),
				". Pe o hartă PUG întreagă durează de regulă 10–20 de secunde (butonul arată „Se generează…”); ",
				"cel mult două exporturi rulează în același timp — al treilea primește mesajul „Prea multe ",
				"exporturi în curs, încearcă peste un minut”.",
			],
			[
				"Butonul ",
				Code("Export …"),
				" de pe rândul fiecărui strat descarcă doar stratul respectiv, în formatul și sistemul alese în ",
				"bară (eticheta lui arată formatul curent).",
			],
		),
		P(
			"Caseta galbenă ",
			Code("Fișierul s-a descărcat, cu avertismente:"),
			" apare când formatul are limite pe care platforma le-a rezolvat singură: Excel taie numele foilor ",
			"la 31 de caractere, Shapefile taie numele câmpurilor la 10 caractere, GML nu acceptă diacritice ",
			"în numele câmpurilor (Ș/Ț devin S/T). Fișierul e complet — avertismentul spune doar ce s-a ",
			"redenumit.",
		),
		Note(
			"info",
			"MBTiles se descarcă din browser pentru hărți de cel mult 40 000 de obiecte; peste, cere ",
			"furnizorului un export de pe server. Pozele de teren nu intră în fișierul de export — doar în ",
			"arhiva „cu atașamente”; adresa de email a celui care a colectat un element iese doar pentru ",
			"membrii primăriei.",
		),
		H3("Importul din CSV, Excel, GML, KML și arhive zip"),
		P(
			"Formularul din tabul ",
			Code("Import"),
			" acceptă, pe lângă GeoPackage și Shapefile, fișiere ",
			Code(".csv"),
			", ",
			Code(".xlsx"),
			" (fiecare foaie devine un strat), ",
			Code(".gml"),
			", ",
			Code(".kml"),
			"/",
			Code(".kmz"),
			" și arhive ",
			Code(".zip"),
			" (un shapefile livrat ca arhivă, chiar și într-un folder).",
		),
		Steps(
			[
				Code("Sistemul de coordonate al fișierului"),
				": la GeoPackage, Shapefile, GML și KML e citit din fișier (îl alegi doar ca să-l suprascrii). ",
				"La CSV și Excel nu există în fișier: cu coloane ",
				Code("lng"),
				"/",
				Code("lat"),
				" alege WGS84; cu coloane ",
				Code("x"),
				"/",
				Code("y"),
				" sau ",
				Code("WKT"),
				" alegerea e obligatorie (de regulă ",
				Code("Stereo 70 (EPSG:3844) — coloane x/y sau WKT"),
				"). Fără ea, stratul e sărit, cu motivul afișat în rezultat — nu ajung puncte greșite pe hartă.",
			],
			[
				Code("Hartă destinație"),
				": o listă cu harta curentă (preselectată) și celelalte hărți la care ai acces — straturile ",
				"din fișier se adaugă la harta aleasă. Ultima opțiune, ",
				Code("Hartă nouă"),
				", creează o hartă nouă: îi scrii numele (precompletat din numele fișierului; identificatorul ",
				"se formează singur și se vede sub câmp) și alegi organizația. Dacă există deja o hartă cu acel ",
				"identificator, importul e oprit și ți se spune să o alegi din listă — nu se intră niciodată ",
				"pe ascuns într-o hartă existentă.",
			],
			[
				"Restul e neschimbat: ",
				Code("Previzualizează"),
				" arată planul fără să scrie nimic, ",
				Code("Importă"),
				" scrie; potrivirea la re-import detectează schimbările.",
			],
		),
		Note(
			"warn",
			"Dacă apeși Importă pe un CSV sau Excel fără să alegi sistemul, primești o informare: „Nu ai ales ",
			"sistemul de coordonate. Dacă fișierul are coloane lng/lat e în regulă; pentru coloane x/y sau WKT ",
			"alege sistemul, altfel stratul va fi sărit.” CSV: separatorul e detectat, zecimalele se scriu cu ",
			"punct.",
		),
	)
}

function SectionFundaluri() {
	return t.section(
		{},
		H2("fundaluri", "Fundaluri de hartă: ortofotoplan, servicii WMS/XYZ"),
		P(
			"Implicit, harta are două fundaluri: ",
			Code("Hartă"),
			" (OpenStreetMap) și ",
			Code("Satelit"),
			" (imagini Esri). Primăria le poate înlocui cu serviciile ei de imagini — ortofotoplanul ANCPI ",
			"prin WMS, un serviciu al consiliului județean, orice server de tile-uri XYZ.",
		),
		Steps(
			[
				Code("Admin"),
				" → Straturi → cardul ",
				Code("Fundaluri de hartă"),
				" → ",
				Code("+ Adaugă fundal"),
				".",
			],
			[
				"Completezi numele (eticheta butonului), tipul ",
				Code("XYZ"),
				" sau ",
				Code("WMS"),
				", adresa (XYZ: un șablon cu ",
				Code("{z}/{x}/{y}"),
				"; WMS: adresa de bază a serviciului, fără parametri, plus straturile WMS cerute) și ",
				"atribuirea — textul sursei, afișat pe hartă și la tipărire.",
			],
			[
				Code("Salvează"),
				". Poți avea până la 10 fundaluri; primul din listă e cel implicit. Ca să revii la ",
				"Hartă/Satelit, ștergi toate rândurile (✕) și salvezi.",
			],
		),
		P(
			"Pe hartă — internă și publică — butoanele configurate iau locul perechii Hartă/Satelit; pagina ",
			"tipărită poartă atribuirea fundalului activ.",
		),
		Note(
			"warn",
			"Doar adrese http(s), fără chei de acces în adresă (parametri de tip key, token, api_key…): ",
			"adresa ajunge în browserul oricui deschide harta publică, deci cheia ar deveni publică. Serviciile ",
			"care cer cheie (MapTiler, Mapbox…) se pun printr-un proxy propriu. Atribuirea e text simplu, fără ",
			"etichete HTML. Un rând gol sau incomplet e refuzat cu un mesaj care spune ce lipsește.",
		),
	)
}

function SectionIconite() {
	return t.section(
		{},
		H2("iconite", "Iconițe pentru straturile de puncte"),
		P(
			"Un strat de puncte se desenează implicit ca cercuri colorate. Pentru obiective publice, stâlpi, ",
			"fântâni sau orice nomenclator cu simbol propriu poți încărca o iconiță.",
		),
		Steps(
			[
				Code("Admin"),
				" → Straturi → rândul stratului → ",
				Code("Editare"),
				" → câmpul ",
				Code("Iconița punctelor (PNG/SVG)"),
				" (apare doar la straturile de puncte).",
			],
			[
				"Alegi un fișier PNG sau SVG de cel mult 64 KB (ideal cu fundal transparent; un simbol de ",
				"64×64 px are câțiva KB) și apeși ",
				Code("Încarcă iconița"),
				". Se aplică imediat, fără să salvezi formularul; previzualizarea apare pe loc.",
			],
			[Code("Șterge iconița"), " (cu confirmare) readuce cercul implicit."],
		),
		P(
			"Iconița se desenează pe hartă la 24 px, centrată pe punct, și apare în rândul stratului, în ",
			"legenda de pe hartă și în pagina tipărită; harta publică o folosește la fel. Fișierele SVG care ",
			"conțin script sau referințe către alte adrese sunt respinse, cu motivul afișat.",
		),
	)
}

function SectionListeValori() {
	return t.section(
		{},
		H2("liste-valori", "Liste de valori pentru atribute (nomenclatoare)"),
		P(
			"Un atribut ca „tip”, „stare” sau „sat” trebuie completat la fel de toți: „beton” și „Beton” sunt ",
			"două valori diferite într-un raport. Lista de valori transformă câmpul liber într-o alegere.",
		),
		Steps(
			[
				Code("Admin"),
				" → Straturi → ",
				Code("Editare"),
				" pe strat → ",
				Code("▸ valori permise per atribut"),
				".",
			],
			[
				"La atributul dorit scrii valorile, câte una pe linie (la atributele numerice — numere). Cel ",
				"mult 200 de valori, fără dubluri — un duplicat e semnalat înainte de trimitere.",
			],
			[Code("Salvează"), " (butonul stratului). Golirea listei scoate restricția."],
		),
		P(
			"Efect: în formularul de element nou și la ",
			Code("Editează atributele"),
			" câmpul devine o listă derulantă cu opțiunea ",
			Code("— nesetat —"),
			" și valorile permise. O valoare veche din import care nu e în listă apare marcată „(valoare ",
			"veche)” — nu dispare, dar la următoarea modificare trebuie aleasă una din listă sau lăsată goală. ",
			"Serverul refuză orice altă valoare, inclusiv de la asistentul AI. Valoarea goală rămâne permisă ",
			"(lista nu obligă completarea), iar importul nu e verificat — datele vechi rămân valide până la ",
			"prima editare.",
		),
	)
}

function SectionFiltreSalvate() {
	return t.section(
		{},
		H2("filtre-salvate", "Filtre salvate (straturi derivate)"),
		P(
			"Un filtru salvat e o vedere a unui strat: „parcelele peste 500 m²”, „stâlpii de beton”, ",
			"„UTR-urile din satul X”. Apare pe hartă ca strat derivat, sub stratul-părinte, cu comutator ",
			"propriu — fără să dublezi datele.",
		),
		Steps(
			[
				Code("Admin"),
				" → Straturi → ",
				Code("Editare"),
				" pe strat → ",
				Code("▸ filtre salvate (straturi derivate)"),
				" → ",
				Code("+ Filtru nou"),
				".",
			],
			[
				"Dai un nume (identificatorul se completează singur) și adaugi condiții cu ",
				Code("+ condiție"),
				": atribut, operator (=, ≠, <, ≤, >, ≥, conține), valoare și interpretarea ei (auto, text, ",
				"număr, da / nu, gol = atribut lipsă). Mai multe condiții se leagă prin ȘI. Cel mult 20 de ",
				"filtre pe strat și 10 condiții pe filtru.",
			],
			[
				Code("Salvează"),
				" (butonul stratului). Greșelile se semnalează cu poziția lor („filtrul #1, clauza #2: …”), ",
				"înainte de trimitere.",
			],
		),
		P(
			"Pe hartă, sub părinte, fiecare filtru are un rând cu numele și condiția lui, în culoare derivată ",
			"din a părintelui, cu butonul ",
			Code("ascuns"),
			"/",
			Code("vizibil"),
			" — poți lăsa doar filtrul aprins și părintele stins. Legenda și pagina tipărită îl arată ca ",
			"„strat — filtru”. Fără SQL, fără export-import.",
		),
		Note(
			"info",
			"„conține” nu ține cont de majuscule, „=” pe text compară exact; pentru date folosește formatul ",
			"AAAA-LL-ZZ. În harta publică apar doar filtrele ale căror atribute sunt toate publice.",
		),
	)
}

function SectionAvize() {
	return t.section(
		{},
		H2("avize", "Nomenclatorul de avize: avize și acorduri pe zone"),
		P(
			"Certificatul de urbanism enumeră avizele și acordurile de care are nevoie solicitantul. Ele depind ",
			"de zonă (UTR) — o zonă de locuit cere altceva decât una industrială — plus câteva generale, ",
			"valabile oriunde. Nomenclatorul le ține într-un singur loc, lângă regulament.",
		),
		Steps(
			[
				Code("Admin"),
				" → tabul ",
				Code("Regim UTR"),
				" → sub tabelul de indicatori, blocul ",
				Code("Avize necesare pe zone"),
				" → ",
				Code("+ Adaugă rând de avize"),
				".",
			],
			[
				"Pe fiecare rând: codul zonei (UTR sau subzonă, sugerat din tabel) sau ",
				Code("*"),
				" pentru avizele generale (toate zonele, inclusiv extravilanul), apoi avizele — câte unul pe ",
				"linie sau despărțite prin „;”.",
			],
			[Code("Salvează regimul"), " — salvează indicatorii, fișele și avizele împreună."],
		),
		P(
			"Efect: în panoul ",
			Code("Regim"),
			" de pe hartă, sub indicatori, apare ",
			Code("Avize și acorduri necesare"),
			" cu lista pentru punctul apăsat (în harta publică: „Avize și acorduri de care vei avea nevoie”); ",
			"în extravilan doar cele generale. O subzonă (ex. In1) folosește și avizele codului de bază (In). ",
			"Când generezi certificatul de urbanism dintr-o solicitare, rubrica „avize necesare” se completează ",
			"automat.",
		),
	)
}

function SectionTopologie() {
	return t.section(
		{},
		H2("topologie", "Verificarea topologică a unui strat"),
		P(
			"Datele venite din CAD au adesea defecte invizibile la ochi: parcele care se suprapun cu câțiva ",
			"centimetri, contururi care se autointersectează, obiecte dublate, goluri până la limita ",
			"localității. Ele strică bilanțurile, raportul de conformitate și analizele. Raportul topologic le ",
			"găsește; nu modifică nimic.",
		),
		Steps(
			[
				Code("Admin"),
				" → Straturi → cardul ",
				Code("Verificare topologică pe strat"),
				" (sub „Conformitate Ordin 904”; apare dacă modulul de conformitate e activ în contract).",
			],
			[
				"Alegi stratul și apeși ",
				Code("Verifică topologia"),
				". Pe straturi mari durează de la câteva secunde la câteva zeci („Se analizează…”).",
			],
			[
				"Primești verdictul — ",
				Code("✅ Fără erori topologice pe ce s-a analizat"),
				" sau ",
				Code("❌ N erori, M avertismente"),
				" — notele (câte obiecte s-au analizat, față de ce strat s-au căutat golurile, ce analize nu ",
				"s-au rulat și de ce) și tabelul cu Fel, Severitate, Arie, Obiecte, Motiv.",
			],
			[
				"Linkurile „obiect 1” / „obiect 2” deschid harta într-un tab nou, centrată pe obiectul ",
				"respectiv; la goluri, „vezi pe hartă” duce la locul golului. ",
				Code("⬇ Descarcă CSV"),
				" salvează tabelul pentru Excel.",
			],
		),
		H3("Ce înseamnă fiecare constatare"),
		t.ul(
			{
				class:
					"list-disc pl-6 text-slate-600 dark:text-slate-300 leading-relaxed flex flex-col gap-1.5 my-3",
			},
			t.li(
				t.b({ class: "text-slate-800 dark:text-slate-200" }, "Geometrie nevalidă"),
				" (eroare) — conturul se autointersectează, are „fundă” sau inel rupt; motivul și locul ",
				"defectului sunt în tabel.",
			),
			t.li(
				t.b({ class: "text-slate-800 dark:text-slate-200" }, "Duplicat"),
				" (eroare) — două obiecte cu exact aceeași geometrie.",
			),
			t.li(
				t.b({ class: "text-slate-800 dark:text-slate-200" }, "Suprapunere"),
				" (eroare) — două poligoane ale aceluiași strat au interior comun, inclusiv unul conținut în ",
				"celălalt. Sub 1 m² e considerată rest de desen („sliver”) și e doar avertisment.",
			),
			t.li(
				t.b({ class: "text-slate-800 dark:text-slate-200" }, "Gol față de limita administrativă"),
				" (avertisment) — teren din interiorul limitei localității neacoperit de strat. Limita e ",
				"stratul PlanSpatial / „Limita UAT” al hărții; fără el, analiza golurilor e sărită.",
			),
		),
		P(
			"„Fără erori” înseamnă exact asta: golurile și resturile de desen sunt avertismente și nu strică ",
			"verdictul. Straturile de parcele au în mod normal goluri — străzile, apele, terenurile ",
			"neîncadrate — deci un număr de goluri nu e o problemă în sine. Straturile de puncte și linii ",
			"primesc doar analizele de geometrii nevalide și duplicate.",
		),
		Note(
			"info",
			"Se afișează cel mult 500 de constatări (totalurile rămân cele reale); fiecare analiză are 120 de ",
			"secunde — dacă expiră, apare la „Analiză nerulată”, iar restul raportului se livrează. Corectarea ",
			"se face pe hartă (Editează geometria) sau prin re-import de la proiectant.",
		),
	)
}

function SectionDepartamente() {
	return t.section(
		{},
		H2("departamente", "Departamente și drepturi pe module, pe rol"),
		P(
			"Două locuri de administrare, cu nume asemănătoare: ",
			Code("Admin"),
			" din meniul principal administrează HARTA de lucru — straturi, import, publicare, regulament. ",
			"Butonul ",
			Code("Administrare"),
			" din dreapta antetului administrează PRIMĂRIA — membri, departamente, drepturi pe module, ",
			"notificări. Îl văd doar proprietarul și administratorii primăriei (owner/admin) și administratorii ",
			"platformei.",
		),
		H3("Departamente"),
		Steps(
			[
				"În ",
				Code("Administrare"),
				", cardul ",
				Code("Membri pe primărie"),
				" (administratorul platformei alege întâi primăria din lista „Primării” — click pe rând ",
				"deschide fișa ei).",
			],
			[
				"Coloana ",
				Code("Departament"),
				": scrie departamentul membrului (text liber, cel mult 80 de caractere — de ex. „Urbanism”, ",
				"„Taxe și impozite”) și apasă Enter sau ieși din câmp: „Departament salvat ✓”. Câmpul sugerează ",
				"departamentele deja folosite; golit, scoate membrul din departament. La adăugarea unui membru ",
				"nou există câmpul „departament (opțional)”.",
			],
		),
		P(
			"Efecte: în inbox-ul solicitărilor apare bifa ",
			Code("Departamentul meu"),
			" (doar pentru cine are departament) — arată cererile asignate colegilor din același departament; ",
			"elementele colectate din teren poartă și departamentul celui care le-a colectat, alături de autor, ",
			"dată și precizia GPS.",
		),
		H3("Drepturi pe module, pe rol"),
		Steps(
			[
				"Tot în ",
				Code("Administrare"),
				", cardul ",
				Code("Setările primăriei"),
				" → „Drepturi pe module, pe rol”: o matrice cu rolurile ",
				Code("Administrator"),
				", ",
				Code("Editor"),
				", ",
				Code("Vizualizator"),
				" și modulele grupate (Portal extern, Urbanism, Hartă, Livrabile, Extensii). Bifă = modulul e ",
				"disponibil rolului.",
			],
			[
				"Debifează ce vrei să oprești pentru un rol (de ex. Rapoarte pentru Editor) și apasă ",
				Code("Salvează drepturile"),
				". ",
				Code("Permite tot"),
				" șterge toate restricțiile.",
			],
		),
		P(
			"Reguli: un rol nu poate primi mai mult decât contractul primăriei; proprietarul (owner) vede ",
			"întotdeauna tot și nu poate fi restricționat; fără „Hartă publică + embed”, sesizările, consultarea ",
			"și documentele publice se opresc și ele pentru acel rol. Membrul restricționat nu mai vede modulul ",
			"în meniu (după re-autentificare), iar dacă ajunge totuși pe pagină primește mesajul „Modulul nu ",
			"este activat pentru rolul tău în această organizație.”",
		),
		H3("Drepturi pe acțiuni, pe rol"),
		P(
			"În aceeași matrice, rândurile ",
			Code("Acțiuni"),
			": export de date, import de date, partajare (publicare, embed-uri, trimiterea hărții pe email) ",
			"și ștergerea obiectelor de pe hartă — comutabile pentru ",
			Code("Editor"),
			" (exportul și pentru ",
			Code("Vizualizator"),
			"); celulele cu „—” nu se pot opri (administratorii și proprietarul au mereu tot). Debifate, ",
			"bara de export, tabul Import și secțiunile de publicare dispar pentru acel rol, iar serverul refuză ",
			"oricum cu „Acțiunea nu este permisă rolului tău în această organizație.” — inclusiv ștergerea unui ",
			"obiect de pe hartă (butonul rămâne, refuzul apare la apăsare). Depublicarea și revocarea unui embed ",
			"rămân permise (retragerea unei expuneri nu se blochează). Conectorul AI nu e afectat de aceste rânduri.",
		),
		H3("Organigrama"),
		Steps(
			[
				"Tot în ",
				Code("Administrare"),
				", cardul ",
				Code("Organigramă"),
				" (sub membri): primăria sus, o coloană per departament — cele din setări și cele scrise pe ",
				"membri (scrise la fel, cu sau fără diacritice, ajung în aceeași coloană) — plus „Fără departament”, ",
				"cu numărul de membri pe fiecare coloană și cardurile membrilor cu rolul lor.",
			],
			[
				"Click pe un card derulează la rândul membrului din tabel (acolo se schimbă rolul). Trage un card ",
				"în altă coloană (cu mouse-ul) ca să-i schimbi departamentul — se salvează imediat, tabelul se ",
				"actualizează. ",
				Code("Tipărește"),
				" deschide organigrama ca pagină curată (A4 peisaj), gata de tipărit sau salvat ca PDF.",
			],
		),
	)
}

function SectionDrepturiStrat() {
	return t.section(
		{},
		H2("drepturi-strat", "Drepturi pe strat și pe atribut, pe rol"),
		P(
			"Pe lângă drepturile pe module (secțiunea de mai sus), proprietarul sau administratorul primăriei ",
			"poate restrânge, strat cu strat, ce văd și ce pot face ",
			Code("Vizualizatorii"),
			" și ",
			Code("Editorii"),
			": un strat întreg ascuns unui rol, un strat pe care editorii doar îl citesc, sau doar câteva ",
			"atribute ascunse (de ex. proprietarul parcelei). Proprietarii și administratorii văd și scriu mereu tot.",
		),
		Steps(
			[
				Code("Admin"),
				" (harta de lucru) → tabul Straturi → ",
				Code("Editează"),
				" pe strat → câmpul „Drepturi pe rol (vizualizatori / editori) și atribute ascunse” — apare doar ",
				"pentru proprietar, administrator și administratorul platformei.",
			],
			[
				"Alege pentru vizualizatori ",
				Code("văd stratul"),
				" sau ",
				Code("nu văd stratul"),
				", pentru editori ",
				Code("pot scrie"),
				", ",
				Code("doar citesc"),
				" sau ",
				Code("nu văd stratul"),
				"; bifează atributele ascunse vizualizatorilor (lista editorilor e pliată, pentru cazuri rare). ",
				Code("Salvează"),
				".",
			],
		),
		P(
			"Efecte pentru rolul restricționat: stratul ascuns nu apare nicăieri — hartă, legendă, tipărire, ",
			"căutare, identificare, export, regim urbanistic, zonificare, conectorul AI; un atribut ascuns lipsește ",
			"din panoul obiectului, tabel, istoric, căutare (nici o căutare cu două cuvinte nu îl confirmă), ",
			"export și din completarea automată a actelor. Pe un strat „doar citesc” editorul primește la ",
			"salvare mesajul „Stratul este doar citire pentru rolul tău în această organizație.”; un atribut ",
			"pe care nu îl vede nu îl poate nici modifica.",
		),
		Note(
			"info",
			"Fără nicio configurare, totul rămâne ca până acum: vizualizatorii văd tot, editorii scriu tot. ",
			"Portalul public nu e atins — acolo contează doar publicarea și „atributele publice”. Conectorul AI ",
			"al primăriei se comportă ca un editor: restricțiile puse pe Editori i se aplică și lui.",
		),
	)
}

function SectionNotificariEmail() {
	return t.section(
		{},
		H2("notificari-email", "Comutatorul notificărilor pe email"),
		P(
			"Tot în ",
			Code("Administrare"),
			" → ",
			Code("Setările primăriei"),
			", bifa ",
			Code("Notificări pe email la solicitări"),
			" decide dacă primăria comunică pe email în fluxul solicitărilor. Se salvează la bifare; o pot ",
			"schimba proprietarul și administratorii primăriei.",
		),
		P(
			"Debifată, oprește TOATE emailurile din fluxul solicitărilor: nici operatorii nu mai primesc ",
			"„solicitare nouă” sau „mesaj nou”, nici cetățenii nu mai primesc schimbările de stare, respingerea ",
			"cu motiv sau anunțul că actul e gata. Inbox-ul și contul cetățeanului rămân sursa de adevăr — ",
			"totul se vede în aplicație. Nu afectează resetarea parolei și nici „Trimite harta pe email”.",
		),
		Note(
			"warn",
			"Cetățeanul unei primării cu notificările oprite află că actul lui e gata doar din contul de pe ",
			"portal — merită spus la ghișeu.",
		),
	)
}

function SectionAsistentAi() {
	return t.section(
		{},
		H2("asistent-ai", "Asistentul AI"),
		P(
			"Platforma poate fi conectată la un asistent AI (de ex. Claude), care răspunde la întrebări ",
			"despre hartă și poate emite documente în locul tău — în limbaj natural, fără cunoștințe GIS.",
		),
		H3("Conectare"),
		Steps(
			[
				"Apasă butonul ",
				Code("⧉ MCP"),
				" din bara de sus (sau ",
				Code("Copiază link MCP"),
				" de pe cardul hărții de pe prima pagină) — link-ul e deja legat de harta respectivă.",
			],
			[
				"Adaugă link-ul în aplicația de AI, la secțiunea de conectori (ex. Claude → Settings → Connectors).",
			],
			["Gata — asistentul lucrează de acum direct pe harta primăriei tale."],
		),
		H3("Ce poți să-l întrebi"),
		t.ul(
			{
				class:
					"list-disc pl-6 text-slate-600 dark:text-slate-300 leading-relaxed flex flex-col gap-1.5 my-3",
			},
			t.li("„Ce regim de înălțime are parcela cu numărul cadastral 82347?”"),
			t.li("„Ce reglementări se aplică la intersecția străzilor X și Y?”"),
			t.li("„Generează un certificat de urbanism pentru parcela 82347, în registrul CU.”"),
			t.li("„Câte parcele intersectează zona de protecție a râului?”"),
			t.li("„Arată-mi documentele emise luna aceasta și statusul lor.”"),
		),
		Note(
			"info",
			"Asistentul folosește aceleași reguli ca aplicația: numerele vin tot din registre, documentele rămân în evidență, totul e auditabil.",
		),
	)
}

// ─────────────────────────────────────────────
// Suport tehnic (decizia 1.8, PIN-008): canalul funcționarului către furnizor. Datele de contact vin
// din variabilele de mediu ale instanței (SUPPORT_EMAIL / SUPPORT_PHONE), expuse prin `/api/health`
// în câmpul `support` — până la publicarea lui de către server, blocul afișează fallback-ul generic.
// Un singur fetch, memorat (promisiunea), partajat cu meniul de cont din app.client.
// ─────────────────────────────────────────────

export interface SupportContact {
	email: string
	phone: string
}
/** Obiect nil (regula codebase-ului: fără `| null` în tipuri) — „neconfigurat" sau „încă neîncărcat". */
export const NIL_SUPPORT: SupportContact = Object.freeze({ email: "", phone: "" })

/** Datele de contact ale instanței; `support_loaded` = răspunsul a sosit (chiar și gol/eșuat). */
export const support_contact = van.state<SupportContact>(NIL_SUPPORT)
export const support_loaded = van.state(false)
let support_loading: Promise<void> | undefined

/** Pornește (o singură dată) încărcarea datelor de contact; safe de chemat de oricâte ori. */
export function ensure_support_contact(): Promise<void> {
	return (support_loading ??= fetch("/api/health", { credentials: "same-origin" })
		.then((r) => r.json() as Promise<{ data?: { support?: Record<string, unknown> } }>)
		.then((b) => {
			const s = b?.data?.support ?? {}
			support_contact.val = {
				email: typeof s.email === "string" ? s.email.trim() : "",
				phone: typeof s.phone === "string" ? s.phone.trim() : "",
			}
		})
		.catch(() => {
			support_contact.val = NIL_SUPPORT
		})
		.finally(() => {
			support_loaded.val = true
		}))
}

/**
 * Blocul cu datele de contact — trei stări montate STATIC (se încarcă / contact / fallback) și
 * comutate prin class (regula Van: nimic nu se detașează). `compact` = varianta din meniul de cont.
 */
export function SupportContactBlock(compact = false): HTMLElement {
	const loaded = () => support_loaded.val
	const has = () => !!(support_contact.val.email || support_contact.val.phone)
	const line = compact ? "text-xs" : "text-sm"
	const link =
		"text-cyan-700 dark:text-cyan-300 hover:underline font-medium break-all " +
		(compact ? "text-xs" : "text-sm")
	return t.div(
		{ class: compact ? "flex flex-col gap-1" : "flex flex-col gap-2 my-3" },
		t.div(
			{ class: () => `${line} text-slate-400 ` + (loaded() ? "hidden" : "") },
			"Se încarcă datele de contact…",
		),
		t.div(
			{ class: () => "flex flex-col gap-1 " + (loaded() && has() ? "" : "hidden") },
			t.div(
				{ class: () => `${line} ` + (support_contact.val.email ? "" : "hidden") },
				t.span({ class: "text-slate-500 dark:text-slate-400" }, "Email: "),
				t.a(
					{ href: () => `mailto:${support_contact.val.email}`, class: link },
					() => support_contact.val.email,
				),
			),
			t.div(
				{ class: () => `${line} ` + (support_contact.val.phone ? "" : "hidden") },
				t.span({ class: "text-slate-500 dark:text-slate-400" }, "Telefon: "),
				t.a(
					{
						href: () => `tel:${support_contact.val.phone.replace(/[^\d+]/g, "")}`,
						class: link,
					},
					() => support_contact.val.phone,
				),
			),
		),
		t.div(
			{
				class: () =>
					`${line} text-slate-600 dark:text-slate-300 ` + (loaded() && !has() ? "" : "hidden"),
			},
			"Datele de contact ale suportului nu sunt configurate pe această instanță — contactați administratorul platformei.",
		),
	)
}

function SectionSuport() {
	void ensure_support_contact()
	return t.section(
		{},
		H2("suport", "Ajutor și suport"),
		P(
			"Pentru orice problemă cu aplicația (nu cu conținutul documentației de urbanism, care rămâne al ",
			"primăriei și al proiectantului) ai două căi: butonul ",
			Code("? Ajutor"),
			" din bara de sus și datele de contact de mai jos — aceleași apar și în meniul de cont, la ",
			Code("Suport"),
			".",
		),
		H3("Butonul „? Ajutor” din bara de sus"),
		P(
			"Apare doar pentru conturile de lucru (membrii unei primării și echipa platformei) și deschide un ",
			"panou lateral în care, fără să părăsești pagina:",
		),
		t.ul(
			{
				class:
					"list-disc pl-6 text-slate-600 dark:text-slate-300 leading-relaxed flex flex-col gap-1.5 my-3",
			},
			t.li(
				"cauți în documentația platformei — rezultatele vin cu capturile din manual și cu linkul către locul respectiv; cele marcate „neinclus în contract” țin de module pe care primăria ta nu le are;",
			),
			t.li(
				"întrebi asistentul (dacă instanța voastră îl are pornit): răspunde din documentație și citează secțiunile pe care se bazează; are cel mult trei răspunsuri într-o conversație, apoi te întreabă dacă s-a rezolvat;",
			),
			t.li(
				"scrii echipei de suport: „Nu, trimite echipei” sau „Vreau să vorbesc cu cineva” transformă conversația într-un tichet; fără asistent pornit, panoul este direct un formular către echipă.",
			),
		),
		H3("Ce ajunge la echipa de suport"),
		P(
			"Tichetul pleacă cu conversația și cu contextul tehnic al paginii: pagina și harta pe care erai, rolul ",
			"tău și modulele primăriei, versiunea aplicației și browserul, ultimele erori tehnice (fără textul ",
			"căutărilor tale). Conversațiile cu asistentul sunt vizibile echipei platformei. Nu scrie în panou date ",
			"personale ale cetățenilor — descrie problema, nu dosarul.",
		),
		P(
			"Răspunsul vine în același fir (panoul „Ajutor” → „Firele tale” sau pagina ",
			Code("#/suport"),
			") și pe email, la adresa contului. Un fir închis se redeschide scriind din nou în el.",
		),
		Note(
			"info",
			"Asistentul din panoul „Ajutor” nu este conectorul din secțiunea „Asistentul AI”: primul răspunde la întrebări despre folosirea platformei, al doilea lucrează pe datele hărții, la cererea primăriei.",
		),
		H3("Suport tehnic prin email și telefon"),
		SupportContactBlock(),
		H3(
			"Cum trimiți o sesizare care se rezolvă repede (pe email; din panoul „Ajutor” contextul tehnic se atașează singur)",
		),
		Steps(
			["Primăria și harta pe care lucrai (numele spațiului de lucru din bara de sus)."],
			["Ce încercai să faci, pas cu pas, și pe ce buton ai apăsat ultima dată."],
			[
				"Ce s-a întâmplat: mesajul exact de eroare (copiat sau captură de ecran) și ce te așteptai să vezi.",
			],
			["Data și ora, browserul sau telefonul folosit și dacă problema se repetă."],
			[
				"Dacă aplicația nu se deschide deloc sau datele nu se salvează, scrie ",
				Code("URGENT"),
				" în subiect — se tratează cu prioritate.",
			],
		),
		H3("La ce te poți aștepta"),
		t.ul(
			{
				class:
					"list-disc pl-6 text-slate-600 dark:text-slate-300 leading-relaxed flex flex-col gap-1.5 my-3",
			},
			t.li(
				"Intervenție și remediere în cel mult 48 de ore de la sesizare; problemele blocante se tratează în aceeași zi lucrătoare.",
			),
			t.li(
				"Disponibilitatea platformei este urmărită permanent de un serviciu extern de monitorizare; întreruperile planificate (întreținere) se anunță în prealabil, pe email.",
			),
			t.li(
				"După un incident major primești o notă scurtă cu ce s-a întâmplat și ce s-a schimbat ca să nu se repete.",
			),
		),
		Note(
			"info",
			"Parola uitată nu are nevoie de suport: butonul „Am uitat parola” din pagina de autentificare trimite un link de resetare (când instanța are email configurat). Înainte de a sesiza „aplicația nu merge”, încearcă o reîncărcare a paginii și un alt browser — dacă problema persistă, sesizarea e urgentă.",
		),
	)
}

// ─────────────────────────────────────────────
// Ghidul „Integrare & API" (F7-T5, REQ-API-001): documentația publică a interfețelor —
// pentru dezvoltatorii/GIS-iștii care consumă datele publicate (QGIS, site-uri, sisteme externe).
// Toate exemplele folosesc tokenul public `?k=` (butonul „Publică" din administrare îl emite).
// ─────────────────────────────────────────────

function SectionApiOgc() {
	return t.section(
		{},
		H2("api-ogc", "OGC API Features (QGIS / ArcGIS)"),
		P(
			"Straturile publice ale unei hărți publicate sunt expuse ca serviciu ",
			t.b("OGC API — Features"),
			" (succesorul standard al WFS) — aplicațiile GIS îl consumă nativ, cu paginare, filtru bbox și istoric.",
		),
		Pre(
			"# landing (descoperirea serviciului) — tokenul vine de la publicarea hărții\n" +
				"curl 'https://<server>/ogc?k=<token>'\n\n" +
				"# colecțiile = straturile publice\n" +
				"curl 'https://<server>/ogc/collections?k=<token>'\n\n" +
				"# datele unui strat (GeoJSON; bbox/limit/offset/datetime opționale)\n" +
				"curl 'https://<server>/ogc/collections/<strat>/items?k=<token>&limit=100&bbox=25.9,44.3,26.3,44.6'",
		),
		H3("Conectare din QGIS"),
		Steps(
			["Layer → Add Layer → ", Code("Add OGC API - Features Layer"), "."],
			[
				"URL: ",
				Code("https://<server>/ogc?k=<token>"),
				" — o găsești gata compusă în Administrare → Publicare, sub linkul public (rândul ",
				Code("OGC API – Features (QGIS)"),
				"). Restul se descoperă singur.",
			],
			["Alege stratul din listă și adaugă-l; QGIS paginează automat."],
		),
		Note(
			"info",
			"Serviciul respectă publicarea per strat și lista de atribute publice — exact ce vede portalul public, nimic în plus. Parametrul datetime (un instant RFC 3339) întoarce starea stratului la acel moment.",
		),
	)
}

function SectionApiDate() {
	return t.section(
		{},
		H2("api-date", "GeoJSON & tile-uri MVT"),
		P("Aceleași date publice, în formatele folosite chiar de viewerul hărții:"),
		Pre(
			"# GeoJSON complet al unui strat public (fără limit primești TOT stratul)\n" +
				"curl 'https://<server>/api/public/layers/<strat>/geojson?k=<token>'\n\n" +
				"# opțional, dacă preferi să-l iei pe bucăți: limit + offset; cât timp mai sunt date,\n" +
				"# colecția poartă truncated: true\n" +
				"curl 'https://<server>/api/public/layers/<strat>/geojson?k=<token>&limit=500&offset=0'\n\n" +
				"# tile-uri vectoriale (Mapbox Vector Tiles) — pentru MapLibre/Leaflet/OpenLayers\n" +
				"https://<server>/api/public/tiles/<strat>/{z}/{x}/{y}.mvt?k=<token>\n\n" +
				"# detaliul + istoricul unui element\n" +
				"curl 'https://<server>/api/public/feature/<id>?k=<token>'\n" +
				"curl 'https://<server>/api/public/feature/<id>/history?k=<token>'",
		),
		Note(
			"info",
			"Răspunsurile REST poartă anveloparea {data, ok, err}; /ogc și tile-urile sunt formate standard, fără anvelopă. ",
			"GeoJSON-ul public întoarce stratul ÎNTREG când nu ceri limit. Dacă preferi să-l iei pe bucăți, ",
			"folosește limit + offset: cât timp mai există date, colecția poartă truncated: true. ",
			"Parametrii invalizi (limit, offset, as_of) sunt respinși cu 400, nu ignorați.",
		),
	)
}

function SectionApiExport() {
	return t.section(
		{},
		H2("api-export", "Export: formate, coordonate, atașamente"),
		P(
			"Aceleași rute ca butoanele de export din administrare — pentru scripturi de sincronizare sau ",
			"livrări periodice. Pe o hartă publică descărcarea e liberă; pe una privată cere sesiunea unui membru.",
		),
		Pre(
			"# toată harta (sau layers=a,b) — implicit GeoPackage în Stereo 70\n" +
				"curl -OJ 'https://<server>/api/maps/<hartă>/export?format=gpkg&srid=3844'\n\n" +
				"# un singur strat; format = gpkg | shp | csv | xlsx | gml | kml | mbtiles\n" +
				"curl -OJ 'https://<server>/api/maps/<hartă>/layers/<strat>/export?format=csv&srid=4326'\n\n" +
				"# arhivă zip cu documentele hărții + pozele de teren (doar cu sesiune de membru)\n" +
				"curl -OJ -b cookies.txt 'https://<server>/api/maps/<hartă>/export?format=gpkg&attachments=1'\n\n" +
				"# import (multipart): file + map (slug, opțional) + source_srid (obligatoriu la CSV/Excel cu x/y sau WKT)\n" +
				"curl -b cookies.txt -F file=@parcele.csv -F map=<hartă> -F source_srid=3844 'https://<server>/api/import'",
		),
		Note(
			"info",
			"Răspunsul e fișierul (Content-Disposition), nu anvelopa JSON; avertismentele non-fatale (nume ",
			"trunchiate, foi redenumite) vin în header-ul X-Export-Warnings, ca JSON codat URL. KML e mereu ",
			"WGS84; MBTiles e mereu Web Mercator, zoom 8–15, cel mult 40 000 de obiecte prin HTTP. ",
			"attachments=1 și mbtiles cer cont de membru (401/403); cel mult două exporturi simultane (429).",
		),
	)
}

function SectionApiInterogari() {
	return t.section(
		{},
		H2("api-interogari", "Filtre salvate, rază, zonificare, iconițe"),
		P(
			"Extensii ale rutelor publice de date — toate cu tokenul ",
			Code("?k="),
			" și cu aceleași reguli de publicare (straturi publice, atribute permise).",
		),
		Pre(
			"# filtre salvate: slug-urile vin din /api/public/map → layers[].saved_filters\n" +
				"curl 'https://<server>/api/public/layers/<strat>/geojson?k=<token>&filter=<filtru>'\n" +
				"https://<server>/api/public/tiles/<strat>/{z}/{x}/{y}.mvt?k=<token>&filter=<filtru>\n\n" +
				"# interogare / căutare pe o rază (metri, geodezic, ≤ 5000) — fiecare rezultat primește distance_m\n" +
				"curl 'https://<server>/api/public/identify?k=<token>&lng=25.5&lat=45.5&radius_m=250'\n" +
				"curl 'https://<server>/api/public/search?k=<token>&q=dispensar&lng=25.5&lat=45.5&radius_m=500'\n\n" +
				"# zonificarea actuală (PUG decupat de PUZ-urile publicate) — doar cu stratul UTR publicat\n" +
				"curl 'https://<server>/api/public/layers/zonificare_actuala/geojson?k=<token>'\n\n" +
				"# iconița unui strat (layers[].icon_url din /api/public/map) — fără token, cache 1 an\n" +
				"https://<server>/api/public/icons/<strat-id>/<fișier>.png",
		),
		Note(
			"info",
			"Un filtru necunoscut sau definit pe un atribut nepublic răspunde 404, identic — nu se poate ",
			"deduce nimic din el. radius_m în afara intervalului (0, 5000] → 400. Zonificarea are ",
			"proprietățile cod, denumire, sursa (PUG/PUZ), act, hcl, layer_slug, feature_id și membrul sources. ",
			"Fundalurile configurate vin în /api/public/map → basemaps (nume, tip xyz/wms, adresă, atribuire).",
		),
		Note(
			"warn",
			"Raportul topologic (GET /api/maps/<hartă>/layers/<strat>/topologie) e o unealtă internă a ",
			"livrării: cere sesiune de membru și modulul de conformitate; nu are variantă publică.",
		),
	)
}

function SectionApiEmbed() {
	return t.section(
		{},
		H2("api-embed", "Embed iFrame configurabil"),
		P(
			"Harta publică se înglobează în orice site printr-un iFrame. Pe lângă linkul de publicare, poți crea ",
			t.b("oricâte embed-uri configurabile"),
			" — fiecare cu propriul link, cu o submulțime de straturi, cu instrumentele alese ",
			"(măsurare, legendă, căutare, 3D, regim, sesizări), opțional blocat la extentul hărții sau cu dată de expirare.",
		),
		Steps(
			["În administrare, pe hartă: secțiunea ", Code("Embed-uri configurabile (iFrame)"), "."],
			["„+ Embed nou” → bifezi straturile și instrumentele → primești link + cod de inserare."],
			["Revocarea unui embed îi omoară linkul imediat, fără să atingă celelalte embed-uri."],
		),
		Pre(
			'<iframe src="https://<server>/embed?k=<token-embed>"\n        width="100%" height="500" style="border:0" loading="lazy"></iframe>',
		),
		Note(
			"info",
			"Tokenul unui embed funcționează și pe /ogc și pe rutele de date publice — serviciul întreg vede doar straturile embed-ului.",
		),
		Note(
			"warn",
			"Publicarea hărții e stăpâna: un embed poate doar să RESTRÂNGĂ straturile publicate, niciodată să le extindă. Dacă republici harta fără un strat, dispare și din embed-urile care îl aveau; îl bifezi la loc și embed-urile își revin singure, cu aceleași link-uri.",
		),
	)
}

function SectionApiLinkuri() {
	return t.section(
		{},
		H2("api-linkuri", "API-uri definibile (linkuri către sisteme externe)"),
		P(
			"Fără dezvoltări software: operatorul definește în administrare linkuri-șablon către alte sisteme, ",
			"iar în panoul elementului selectat apar butoane care le deschid pre-completate cu datele elementului.",
		),
		Pre(
			"https://sistem.extern.ro/cauta?x={lng}&y={lat}&nr={attr:nr_cadastral}\n" +
				"# {lng} {lat}  → coordonatele elementului selectat\n" +
				"# {attr:cheie} → valoarea atributului „cheie” al elementului",
		),
		Note("info", "Doar adrese http(s); valorile sunt codate URL automat."),
	)
}

function SectionApiStabilitate() {
	return t.section(
		{},
		H2("api-stabilitate", "Ce e stabil, ce e intern"),
		t.ul(
			{
				class:
					"list-disc pl-6 text-slate-600 dark:text-slate-300 leading-relaxed flex flex-col gap-1.5 my-3",
			},
			t.li(
				t.b("Contract public (stabil): "),
				"rutele /ogc, /api/public/* (geojson, tiles, feature, search, regim, documents), /embed și conectorul MCP.",
			),
			t.li(
				t.b("Intern (se poate schimba fără preaviz): "),
				"restul rutelor /api/* — sunt interfața aplicației, gardate pe sesiune, nu API public.",
			),
			t.li(
				t.b("Autorizare: "),
				"tot ce e public umblă pe tokenul semnat ",
				Code("?k="),
				" — revocabil oricând din administrare (depublicare / revocarea embed-ului).",
			),
		),
	)
}

// ─────────────────────────────────────────────
// TOC (left sidebar)
// ─────────────────────────────────────────────
function Toc(guide: () => Guide) {
	return t.nav(
		{
			class:
				"hidden lg:block sticky top-14 self-start w-52 flex-shrink-0 max-h-[calc(100vh-4rem)] overflow-y-auto",
		},
		// Doar cuprinsul ghidului ACTIV — grupurile sunt noduri stabile, comutate prin display (F8).
		...TOC_GROUPS.map((group) =>
			t.div(
				{
					class: "mb-5",
					style: () => `display:${guide() === group.guide ? "block" : "none"}`,
				},
				t.p(
					{
						class:
							"text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 px-2",
					},
					group.title,
				),
				t.ul(
					{ class: "space-y-0.5" },
					...group.items.map((item) =>
						t.li(
							t.a(
								{
									href: `#${item.id}`,
									onclick: scroll_to_id(item.id),
									class:
										"block px-2 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-cyan-700 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors",
								},
								item.label,
							),
						),
					),
				),
			),
		),
	)
}

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────
export function DocsPage() {
	// Deep-link pe secțiune: #/docs?s=<id> — fragmentul clasic (#id) e ocupat de hash-router,
	// deci linkul „Ajutor” din portalul public țintește prin query. Scroll după montare.
	const q = location.hash.split("?")[1] ?? ""
	const target = new URLSearchParams(q).get("s") ?? ""
	if (target) {
		setTimeout(() => document.getElementById(target)?.scrollIntoView(), 0)
	}
	// Ghidul afișat: alegerea explicită (deep-link / tab) bate default-ul pe audiență —
	// staff (membru al unui org sau rol de platformă) → funcționar, restul → cetățean.
	// Default-ul e o FUNCȚIE (nu un snapshot): sesiunea se hidratează async, iar tab-ul
	// se corectează singur când sosește (choice rămâne gol până la primul click).
	const choice = van.state<Guide | "">(SECTION_GUIDE[target] ?? "")
	const guide = (): Guide => choice.val || (is_staff() ? "functionar" : "cetatean")
	const tab = (g: Guide, label: string) =>
		t.button(
			{
				class: () =>
					"px-4 py-1.5 rounded-full text-sm font-semibold transition cursor-pointer " +
					(guide() === g
						? "bg-cyan-700 text-white"
						: "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-cyan-300 hover:text-cyan-700"),
				onclick: () => {
					choice.val = g
				},
			},
			label,
		)
	return t.div(
		{
			class:
				"min-h-screen bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 antialiased",
		},
		// Hero banner
		t.div(
			{
				class:
					"border-b border-slate-200 dark:border-slate-700 bg-gradient-to-b from-cyan-50 to-white dark:from-cyan-950 dark:to-slate-900",
			},
			t.div(
				{ class: "max-w-6xl mx-auto px-6 py-10" },
				t.div(
					{
						class:
							"inline-flex items-center gap-2 rounded-full border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950 px-3.5 py-1.5 text-sm font-medium text-cyan-800 dark:text-cyan-300 mb-4",
					},
					t.span({ class: "w-2 h-2 rounded-full bg-cyan-500" }),
					"Ghid de utilizare",
				),
				t.h1(
					{
						class:
							"text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100",
					},
					"Cum folosești ",
					t.span({ class: "text-cyan-700 dark:text-cyan-300" }, "Urbanism"),
				),
				t.p(
					{ class: "mt-2 text-lg text-slate-600 dark:text-slate-300 max-w-2xl" },
					"Ghid pentru cetățeni (harta publică, regimul urbanistic, solicitări) și pentru funcționari (documente, registre, solicitări, asistent AI) — pas cu pas.",
				),
				// Comutatorul de ghid: ambele rămân PUBLICE (transparență); tab-ul schimbă doar
				// ce se afișează. Default pe audiență, deep-link-ul ?s= alege singur tab-ul corect.
				t.div(
					{ class: "mt-5 flex gap-2" },
					tab("cetatean", "🧭 Ghid cetățean"),
					tab("functionar", "🏛️ Ghid funcționar"),
					tab("api", "🔌 Integrare & API"),
				),
			),
		),

		// Body: TOC + conținutul ghidului activ (containere stabile, comutate prin display — F8).
		t.div(
			{ class: "max-w-6xl mx-auto px-6 py-12 flex gap-12" },
			Toc(guide),
			t.main(
				{ class: "flex-1 min-w-0" },
				t.div(
					{ style: () => `display:${guide() === "cetatean" ? "block" : "none"}` },
					GuideHeading(
						"🧭",
						"Ghid cetățean",
						"Consultarea hărții publice a primăriei și trimiterea de solicitări — fără cunoștințe tehnice.",
					),
					SectionCetatean(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionCetateanHarta(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionCetateanRegim(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionCetateanSolicitari(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionCetateanConsultare(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionCetateanCont(),
				),
				t.div(
					{ style: () => `display:${guide() === "functionar" ? "block" : "none"}` },
					GuideHeading(
						"🏛️",
						"Ghid funcționar",
						"Back-office-ul primăriei: harta de lucru, solicitările cetățenilor, documente cu număr de registru.",
					),
					SectionCeEste(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionPrimiiPasi(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionHarta(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionCoordonate(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionRaza(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionZonificare(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionLipire(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionSolicitariOperator(),
					SectionNotificariOperator(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionRapoarte(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionDocumente(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionRegistre(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionSabloane(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionAdminPublicare(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionExportImport(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionFundaluri(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionIconite(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionListeValori(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionFiltreSalvate(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionAvize(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionTopologie(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionDepartamente(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionNotificariEmail(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionDrepturiStrat(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionAsistentAi(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionSuport(),
				),
				t.div(
					{ style: () => `display:${guide() === "api" ? "block" : "none"}` },
					GuideHeading(
						"🔌",
						"Integrare & API",
						"Interfețele publice ale platformei: OGC pentru aplicații GIS, GeoJSON/MVT pentru web, embed-uri configurabile și linkuri către sisteme externe.",
					),
					SectionApiOgc(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionApiDate(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionApiExport(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionApiInterogari(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionApiEmbed(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionApiLinkuri(),
					t.hr({ class: "border-slate-100 dark:border-slate-800 my-2" }),
					SectionApiStabilitate(),
				),
			),
		),

		// Footer
		t.footer(
			{ class: "border-t border-slate-200 dark:border-slate-700" },
			t.div(
				{
					class:
						"max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4",
				},
				t.div(
					{ class: "flex items-center gap-2" },
					t.span(
						{ class: "text-cyan-700 dark:text-cyan-300 font-extrabold tracking-tight" },
						"Urbanism",
					),
					t.span(
						{ class: "text-slate-400 dark:text-slate-500 text-sm" },
						"· platformă GIS pentru administrația publică",
					),
				),
				t.div(
					{ class: "flex items-center gap-6 text-sm text-slate-500 dark:text-slate-400" },
					t.a({ href: "#/map", class: "hover:text-slate-900" }, "Hartă"),
					t.a({ href: "#/documents", class: "hover:text-slate-900" }, "Documente"),
					t.a({ href: "#/admin", class: "hover:text-slate-900" }, "Admin"),
					// Suport e în ghidul funcționarului: comută tab-ul (dacă e altul activ), apoi derulează.
					t.a(
						{
							href: "#/docs?s=suport",
							class: "hover:text-slate-900",
							onclick: (e: Event) => {
								e.preventDefault()
								choice.val = "functionar"
								setTimeout(
									() => document.getElementById("suport")?.scrollIntoView({ behavior: "smooth" }),
									0,
								)
							},
						},
						"Suport",
					),
					t.a({ href: "/api/health", class: "hover:text-slate-900" }, "Status"),
				),
			),
		),
	)
}
